import React, { useCallback, useEffect, useState, createElement } from 'react';
import { DollarSignIcon, SearchIcon, AlertCircleIcon, ClockIcon, FileTextIcon, PlusIcon, EditIcon, TrashIcon, UserIcon, CheckCircleIcon, PercentIcon, SettingsIcon, CreditCardIcon, DownloadIcon, FileMinusIcon, ArrowRightLeftIcon, CalculatorIcon, BanIcon, UndoIcon, FilterIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { supabaseAdmin } from '../lib/supabase';
import { Invoice, CreditNote } from '../types';
import { useApp } from '../context/AppContext';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import jsPDF from 'jspdf';
import { useAuth } from '../context/AuthContext';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
type TabType = 'facturas' | 'notas-credito' | 'aplicar-nc' | 'historial-nc';
type FormData = {
  numeroFactura: string;
  farmacia: string;
  vendedor: string;
  vendedorId: string;
  fechaEmision: string;
  fechaVencimiento: string;
  subTotal: string;
  itbis: string;
  monto: string;
  saldoPendiente: string;
  estado: Invoice['estado'];
  ppPct: string;
};
type NCFormData = {
  numero: string;
  farmacia: string;
  vendedor: string;
  vendedorId: string;
  monto: string;
  notas: string;
};
const emptyForm: FormData = {
  numeroFactura: '',
  farmacia: '',
  vendedor: '',
  vendedorId: '',
  fechaEmision: new Date().toISOString().split('T')[0],
  fechaVencimiento: '',
  subTotal: '',
  itbis: '',
  monto: '',
  saldoPendiente: '',
  estado: 'pendiente',
  ppPct: ''
};
const emptyNCForm: NCFormData = {
  numero: '',
  farmacia: '',
  vendedor: '',
  vendedorId: '',
  monto: '',
  notas: ''
};
// ── PDF Receipt ───────────────────────────────────────────────────────────────
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png';
async function getLogoBase64(): Promise<string | null> {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = LOGO_URL;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || 100;
    canvas.height = img.naturalHeight || 100;
    canvas.getContext('2d')!.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
export async function downloadReciboPago(farmacia: string, invoicesPaid: {
  numeroFactura: string;
  monto: number;
  abono: number;
  descuento?: number;
}[], totalPagado: number, metodoPago: string) {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a5'
  });
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const ml = 10;
  const mr = 10;
  const fmtNum = (n: number) => new Intl.NumberFormat('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(n);
  const fmtDate = (d: Date) => d.toLocaleDateString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
  const logoBase64 = await getLogoBase64();
  if (logoBase64) doc.addImage(logoBase64, 'PNG', ml, 4, 20, 20);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('Miladys Rodriguez & Aso.', ml + 24, 12);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text('Av. Antonio Guzmán Nº 102 (Altos) • Tels. 809-247-2104 • 809-247-1006', ml + 24, 18);
  doc.text('Fax: 809-247-5098 • Santiago, República Dominicana  •  RNC 130-329291', ml + 24, 23);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('Fecha:', pw - mr - 44, 12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(fmtDate(new Date()), pw - mr - 20, 12);
  doc.setFillColor(220, 235, 255);
  doc.setDrawColor(41, 82, 196);
  doc.setLineWidth(0.6);
  doc.rect(pw - mr - 46, 14, 46, 10, 'FD');
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('RD$ ' + fmtNum(totalPagado), pw - mr - 23, 21, {
    align: 'center'
  });
  const receiptNum = Math.floor(Math.random() * 90000 + 10000).toString();
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(200, 30, 30);
  doc.text(receiptNum, pw - mr, 32, {
    align: 'right'
  });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('NUMERO DE RECIBO  M', ml, 30);
  doc.setDrawColor(13, 27, 70);
  doc.setLineWidth(0.9);
  doc.line(ml, 31.5, pw - mr, 31.5);
  doc.setLineWidth(0.3);
  doc.line(ml, 33, pw - mr, 33);
  let y = 40;
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text('He recibido de', ml, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(farmacia, ml + 34, y);
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);
  doc.line(ml + 33, y + 1, pw - mr, y + 1);
  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text('El valor de', ml, y);
  doc.line(ml + 24, y + 1, pw - mr, y + 1);
  y += 7;
  doc.setFontSize(7.5);
  doc.text('Para ser abonado a su cuenta MILADYS RODRIGUEZ & ASOCIADOS, en la siguiente forma:', ml, y);
  y += 5;
  const cols = ['FACTURA Nº', 'FECHA', 'VALOR', 'CRÉDITO', 'DESCUENTO', 'MONTO'];
  const colW = [35, 28, 32, 30, 30, 35];
  const tableW = 190;
  const rowH = 7.5;
  doc.setFillColor(13, 27, 70);
  doc.rect(ml, y, tableW, rowH, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  let x = ml;
  cols.forEach((col, i) => {
    doc.text(col, x + colW[i] / 2, y + rowH - 2.2, {
      align: 'center'
    });
    x += colW[i];
  });
  y += rowH;
  const totalRows = Math.max(invoicesPaid.length, 5);
  for (let i = 0; i < totalRows; i++) {
    const isEven = i % 2 === 0;
    doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 255);
    doc.rect(ml, y, tableW, rowH, 'F');
    doc.setDrawColor(180, 200, 220);
    doc.setLineWidth(0.2);
    doc.rect(ml, y, tableW, rowH, 'S');
    if (i < invoicesPaid.length) {
      const inv = invoicesPaid[i];
      let cx = ml;
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.text(inv.numeroFactura, cx + colW[0] / 2, y + rowH - 2.2, {
        align: 'center'
      });
      cx += colW[0];
      doc.setFont('helvetica', 'normal');
      doc.text(fmtDate(new Date()), cx + colW[1] / 2, y + rowH - 2.2, {
        align: 'center'
      });
      cx += colW[1];
      doc.text(fmtNum(inv.monto), cx + colW[2] / 2, y + rowH - 2.2, {
        align: 'center'
      });
      cx += colW[2];
      doc.text(fmtNum(inv.abono), cx + colW[3] / 2, y + rowH - 2.2, {
        align: 'center'
      });
      cx += colW[3];
      if (inv.descuento && inv.descuento > 0) {
        doc.setTextColor(5, 120, 60);
        doc.text(fmtNum(inv.descuento), cx + colW[4] / 2, y + rowH - 2.2, {
          align: 'center'
        });
        doc.setTextColor(0, 0, 0);
      }
      cx += colW[4];
      const neto = inv.abono - (inv.descuento || 0);
      doc.setFont('helvetica', 'bold');
      doc.text(fmtNum(neto > 0 ? neto : inv.abono), cx + colW[5] / 2, y + rowH - 2.2, {
        align: 'center'
      });
      doc.setFont('helvetica', 'normal');
    }
    y += rowH;
  }
  doc.setDrawColor(13, 27, 70);
  doc.setLineWidth(0.5);
  doc.line(ml, y, ml + tableW, y);
  const subTotalVal = Math.round(totalPagado / 1.18 * 100) / 100;
  const itbisVal = Math.round((totalPagado - subTotalVal) * 100) / 100;
  const summaryRowH = 7.5;
  y += 2; // spacing before summary
  // Sub Total row
  doc.setFillColor(248, 250, 252);
  doc.rect(ml, y, tableW, summaryRowH, 'F');
  doc.setDrawColor(180, 200, 220);
  doc.setLineWidth(0.15);
  doc.rect(ml, y, tableW, summaryRowH, 'S');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text('SUB TOTAL:', ml + tableW - 58, y + summaryRowH / 2 + 1, {
    align: 'left'
  });
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('RD$ ' + fmtNum(subTotalVal), ml + tableW - 2, y + summaryRowH / 2 + 1, {
    align: 'right'
  });
  y += summaryRowH;
  // ITBIS row
  doc.setFillColor(248, 250, 252);
  doc.rect(ml, y, tableW, summaryRowH, 'F');
  doc.setDrawColor(180, 200, 220);
  doc.setLineWidth(0.15);
  doc.rect(ml, y, tableW, summaryRowH, 'S');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text('ITBIS 18%:', ml + tableW - 58, y + summaryRowH / 2 + 1, {
    align: 'left'
  });
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('RD$ ' + fmtNum(itbisVal), ml + tableW - 2, y + summaryRowH / 2 + 1, {
    align: 'right'
  });
  y += summaryRowH;
  // TOTAL row
  const totalSummaryH = 9;
  doc.setFillColor(220, 235, 255);
  doc.rect(ml, y, tableW, totalSummaryH, 'F');
  doc.setDrawColor(13, 27, 70);
  doc.setLineWidth(0.4);
  doc.rect(ml, y, tableW, totalSummaryH, 'S');
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 27, 70);
  doc.text('TOTAL:', ml + tableW - 58, y + totalSummaryH / 2 + 1, {
    align: 'left'
  });
  doc.text('RD$ ' + fmtNum(totalPagado), ml + tableW - 2, y + totalSummaryH / 2 + 1, {
    align: 'right'
  });
  y += totalSummaryH;
  y += 9;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);
  doc.text('Efectivo', ml, y);
  doc.line(ml + 16, y + 1, ml + 56, y + 1);
  if (metodoPago === 'efectivo') {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 120, 60);
    doc.text(fmtNum(totalPagado), ml + 17, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
  }
  doc.text('Cheque Nº', ml + 60, y);
  doc.line(ml + 76, y + 1, ml + 110, y + 1);
  if (metodoPago === 'cheque') {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 120, 60);
    doc.text('✓', ml + 77, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
  }
  doc.text('Banco', ml + 112, y);
  doc.line(ml + 122, y + 1, ml + 152, y + 1);
  if (metodoPago === 'transferencia') {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(5, 120, 60);
    doc.text('Transferencia', ml + 123, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
  }
  doc.text('Futurista', ml + 154, y);
  doc.line(ml + 165, y + 1, pw - mr, y + 1);
  y += 14;
  doc.setDrawColor(0);
  doc.setLineWidth(0.3);
  doc.line(ml, y, ml + 65, y);
  doc.line(pw - mr - 65, y, pw - mr, y);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text('Conforme Cliente', ml + 32, y + 5, {
    align: 'center'
  });
  doc.text('Recibido Conforme', pw - mr - 32, y + 5, {
    align: 'center'
  });
  doc.setFillColor(13, 27, 70);
  doc.rect(0, ph - 9, pw, 9, 'F');
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(123, 163, 216);
  doc.text('Miladys Rodriguez & Asoc. © 2025 — Sistema de Control de Vendedores', pw / 2, ph - 3.5, {
    align: 'center'
  });
  doc.save(`recibo-${farmacia.replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`);
}
export function CuentasCobrarPage() {
  const {
    clients,
    users,
    orders
  } = useApp();
  const {
    user,
    addActivityLog
  } = useAuth();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const canSeeDiscount = isAdmin || user?.permisos?.descuento_pronto_pago === true || user?.permisos?.descuento_pronto_pago === undefined;
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('facturas');
  // Invoice state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendedorFilter, setVendedorFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDiscountConfigOpen, setIsDiscountConfigOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [facturaError, setFacturaError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;
  // Credit Notes state
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  // ── Name normalization helpers (used across tabs) ──────────────────────────
  // Aggressive normalization: lowercase, collapse whitespace, strip punctuation & accents
  const normalizeName = (s: string) => s.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') // strip accents
  .replace(/[^a-z0-9\s]/g, '') // strip punctuation
  .replace(/\s+/g, ' ') // collapse whitespace
  .trim();
  // Looser match: checks if two names refer to the same farmacia
  const farmaciaMatch = (a: string, b: string) => {
    if (!a || !b) return false;
    const na = normalizeName(a);
    const nb = normalizeName(b);
    if (na === nb) return true;
    // Fallback: one contains the other (handles partial name differences)
    if (na.length > 3 && nb.length > 3) {
      if (na.includes(nb) || nb.includes(na)) return true;
    }
    // Fallback: compare first N significant characters (handles trailing suffixes like "SRL" vs "EIRL")
    const wordsA = na.split(' ').filter(w => w.length > 1);
    const wordsB = nb.split(' ').filter(w => w.length > 1);
    if (wordsA.length >= 2 && wordsB.length >= 2) {
      // If the first 2 significant words match, consider it a match
      if (wordsA[0] === wordsB[0] && wordsA[1] === wordsB[1]) return true;
    }
    return false;
  };
  // Derived lists — must be after creditNotes useState
  // Collect all farmacia names from clients, invoices, and credit notes
  const allFarmaciaNames = [...clients.map(c => c.nombre), ...invoices.filter(inv => inv.farmacia).map(inv => inv.farmacia), ...creditNotes.filter(nc => nc.farmacia).map(nc => nc.farmacia)];
  // Deduplicate using normalizeName to avoid near-duplicates
  const seenNormalized = new Map<string, string>();
  allFarmaciaNames.forEach(name => {
    const norm = normalizeName(name);
    if (!seenNormalized.has(norm)) {
      seenNormalized.set(norm, name);
    }
  });
  const FARMACIAS = [...seenNormalized.values()].sort();
  const VENDEDORES = users.filter(u => u.activo && u.rolNombre === 'Vendedor').map(u => u.nombre);
  const [isNCModalOpen, setIsNCModalOpen] = useState(false);
  const [isNCDeleteModalOpen, setIsNCDeleteModalOpen] = useState(false);
  const [editingNC, setEditingNC] = useState<CreditNote | null>(null);
  const [ncFormData, setNCFormData] = useState<NCFormData>(emptyNCForm);
  const [ncError, setNCError] = useState('');
  const [ncPage, setNCPage] = useState(1);
  const [ncSearchQuery, setNCSearchQuery] = useState('');
  // Estado de Cuenta state
  const [showEstadoCuenta, setShowEstadoCuenta] = useState(false);
  const [estadoCuentaClient, setEstadoCuentaClient] = useState('');
  const [isGeneratingEstadoCuenta, setIsGeneratingEstadoCuenta] = useState(false);
  // Application state
  const [appFarmacia, setAppFarmacia] = useState('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [selectedNCIds, setSelectedNCIds] = useState<string[]>([]);
  const [isApplying, setIsApplying] = useState(false);
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  // NC Applications history state
  const [ncApplications, setNcApplications] = useState<any[]>([]);
  const [anularNCApp, setAnularNCApp] = useState<any | null>(null);
  const [isAnulandoNC, setIsAnulandoNC] = useState(false);
  const [ncAppPage, setNcAppPage] = useState(1);
  // Payment modal state
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentClient, setPaymentClient] = useState('');
  const [paymentAmounts, setPaymentAmounts] = useState<Record<string, number>>({});
  const [paymentMetodo, setPaymentMetodo] = useState('efectivo');
  const [paymentDone, setPaymentDone] = useState(false);
  const [lastPaymentData, setLastPaymentData] = useState<{
    farmacia: string;
    invoices: {
      numeroFactura: string;
      monto: number;
      abono: number;
    }[];
    total: number;
  } | null>(null);
  const [isDownloadingRecibo, setIsDownloadingRecibo] = useState(false);
  const [isCotizacionModalOpen, setIsCotizacionModalOpen] = useState(false);
  const [cotizacionSearch, setCotizacionSearch] = useState('');
  const [cotizaciones, setCotizaciones] = useState<any[]>([]);
  const [isLoadingCotizaciones, setIsLoadingCotizaciones] = useState(false);
  // Discount config
  const [discountPct, setDiscountPct] = useState(() => parseFloat(localStorage.getItem('mr_discount_pct') || '5'));
  const [discountDays, setDiscountDays] = useState(() => parseInt(localStorage.getItem('mr_discount_days') || '60', 10));
  const [discountConfigDraft, setDiscountConfigDraft] = useState(() => ({
    pct: parseFloat(localStorage.getItem('mr_discount_pct') || '5'),
    days: parseInt(localStorage.getItem('mr_discount_days') || '60', 10)
  }));
  // Load cotizaciones from DB when modal opens
  useEffect(() => {
    if (!isCotizacionModalOpen) return;
    const loadCotizaciones = async () => {
      setIsLoadingCotizaciones(true);
      try {
        const {
          data
        } = await supabaseAdmin.from('cotizaciones').select('id,numero_cotizacion,farmacia,vendedor,fecha,items,total').order('fecha', {
          ascending: false
        }).limit(100);
        if (data) setCotizaciones(data);
      } catch (err) {
        console.error('Error loading cotizaciones:', err);
      } finally {
        setIsLoadingCotizaciones(false);
      }
    };
    loadCotizaciones();
  }, [isCotizacionModalOpen]);
  useEffect(() => {
    const loadDiscountFromDB = async () => {
      try {
        const {
          data
        } = await supabaseAdmin.from('app_settings').select('key, value').in('key', ['discount_pct', 'discount_days']);
        if (data && data.length > 0) {
          const pctRow = data.find((r: any) => r.key === 'discount_pct');
          const daysRow = data.find((r: any) => r.key === 'discount_days');
          const pct = pctRow ? parseFloat(pctRow.value) : 5;
          const days = daysRow ? parseInt(daysRow.value, 10) : 60;
          setDiscountPct(pct);
          setDiscountDays(days);
          setDiscountConfigDraft({
            pct,
            days
          });
          localStorage.setItem('mr_discount_pct', String(pct));
          localStorage.setItem('mr_discount_days', String(days));
        }
      } catch {}
    };
    loadDiscountFromDB();
  }, []);
  const saveDiscountConfig = async (pct: number, days: number) => {
    try {
      await supabaseAdmin.from('app_settings').upsert([{
        key: 'discount_pct',
        value: String(pct),
        updated_at: new Date().toISOString()
      }, {
        key: 'discount_days',
        value: String(days),
        updated_at: new Date().toISOString()
      }]);
    } catch {}
    localStorage.setItem('mr_discount_pct', String(pct));
    localStorage.setItem('mr_discount_days', String(days));
    setDiscountPct(pct);
    setDiscountDays(days);
    window.dispatchEvent(new CustomEvent('discount-config-updated', {
      detail: {
        pct,
        days
      }
    }));
  };
  const mapInvoice = (row: any): Invoice => {
    const fechaVencimiento = new Date(row.fecha_vencimiento);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let estado: Invoice['estado'] = row.estado;
    if (estado !== 'pagada' && fechaVencimiento < today && row.saldo_pendiente > 0) estado = 'vencida';
    return {
      id: row.id,
      numeroFactura: row.numero_factura,
      farmacia: row.farmacia,
      vendedor: row.vendedor,
      vendedorId: row.vendedor_id || undefined,
      fechaEmision: new Date(row.fecha_emision),
      fechaVencimiento,
      subTotal: row.sub_total || 0,
      itbis: row.itbis || 0,
      monto: row.monto,
      saldoPendiente: row.saldo_pendiente,
      estado,
      ppPct: row.pp_pct != null ? Number(row.pp_pct) : null
    };
  };
  const mapCreditNote = (row: any): CreditNote => ({
    id: row.id,
    numero: row.numero,
    farmacia: row.farmacia,
    vendedor: row.vendedor,
    vendedorId: row.vendedor_id || undefined,
    monto: row.monto,
    montoDisponible: row.monto_disponible,
    facturaAplicada: row.factura_aplicada || null,
    estado: row.estado,
    fechaCreacion: new Date(row.fecha_creacion),
    notas: row.notas || ''
  });
  const loadInvoices = async () => {
    setIsLoading(true);
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('invoices').select('id,numero_factura,farmacia,vendedor,vendedor_id,fecha_emision,fecha_vencimiento,sub_total,itbis,monto,saldo_pendiente,estado,pp_pct').order('fecha_emision', {
        ascending: false
      }).limit(2000);
      if (error) {
        console.error('[CuentasCobrar] Error cargando facturas:', error.message, error);
      }
      if (data) {
        const mapped = data.map(mapInvoice);
        setInvoices(mapped);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const toFix = data.filter((row: any) => {
          const venc = new Date(row.fecha_vencimiento);
          return (row.estado === 'pendiente' || row.estado === 'parcial') && venc < today && row.saldo_pendiente > 0;
        });
        if (toFix.length > 0) {
          Promise.all(toFix.map((row: any) => supabaseAdmin.from('invoices').update({
            estado: 'vencida'
          }).eq('id', row.id))).catch(() => {});
        }
      }
    } finally {
      setIsLoading(false);
    }
  };
  const loadCreditNotes = async () => {
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('credit_notes').select('id,numero,farmacia,vendedor,vendedor_id,monto,monto_disponible,factura_aplicada,estado,fecha_creacion,notas').order('fecha_creacion', {
        ascending: false
      }).limit(300);
      if (error) {
        console.error('[CuentasCobrar] Error cargando notas de crédito:', error.message, error);
      }
      if (data) setCreditNotes(data.map(mapCreditNote));
    } catch (err) {
      console.error('[CuentasCobrar] Exception cargando NC:', err);
    }
  };
  const loadNCApplications = async () => {
    try {
      const {
        data
      } = await supabaseAdmin.from('nc_applications').select('*').order('created_at', {
        ascending: false
      }).limit(200);
      if (data) setNcApplications(data);
    } catch {}
  };
  const handleAnularNCApplication = async () => {
    if (!anularNCApp) return;
    setIsAnulandoNC(true);
    try {
      const ncApp = anularNCApp;
      // 1. Restore invoice saldo_pendiente
      const restoredAmount = Number(ncApp.total_original) - Number(ncApp.total_a_pagar);
      const {
        data: currentInvoice
      } = await supabaseAdmin.from('invoices').select('id, saldo_pendiente, monto').eq('id', ncApp.invoice_id).single();
      if (currentInvoice) {
        const newBalance = Math.min(currentInvoice.monto, Number(currentInvoice.saldo_pendiente) + restoredAmount);
        const newStatus = newBalance <= 0 ? 'pagada' : newBalance < currentInvoice.monto ? 'parcial' : 'pendiente';
        await supabaseAdmin.from('invoices').update({
          saldo_pendiente: newBalance,
          estado: newStatus
        }).eq('id', ncApp.invoice_id);
      }
      // 2. Restore credit note monto_disponible
      const ncMontoAplicado = Number(ncApp.nc_monto_aplicado) || 0;
      if (ncMontoAplicado > 0) {
        const {
          data: currentNC
        } = await supabaseAdmin.from('credit_notes').select('id, monto, monto_disponible').eq('id', ncApp.credit_note_id).single();
        if (currentNC) {
          const newNCBalance = Math.min(currentNC.monto, Number(currentNC.monto_disponible) + ncMontoAplicado);
          const newNCStatus = newNCBalance >= currentNC.monto ? 'disponible' : 'parcial';
          await supabaseAdmin.from('credit_notes').update({
            monto_disponible: newNCBalance,
            estado: newNCStatus,
            factura_aplicada: null
          }).eq('id', ncApp.credit_note_id);
        }
      }
      // 3. Propagate to related visits — restore saldo in facturasDetalle
      try {
        const {
          data: relatedVisits
        } = await supabaseAdmin.from('visits').select('id, cobro').eq('cliente_nombre', ncApp.farmacia).not('cobro', 'is', null).limit(500);
        if (relatedVisits && relatedVisits.length > 0) {
          for (const visit of relatedVisits) {
            const cobro = visit.cobro as any;
            if (!cobro?.facturasDetalle || !Array.isArray(cobro.facturasDetalle)) continue;
            const matchIdx = cobro.facturasDetalle.findIndex((fd: any) => fd.facturaId === ncApp.invoice_id || fd.numeroFactura && fd.numeroFactura === ncApp.invoice_numero);
            if (matchIdx === -1) continue;
            const updatedDetalle = cobro.facturasDetalle.map((fd: any, idx: number) => {
              if (idx !== matchIdx) return fd;
              return {
                ...fd,
                saldoNuevo: currentInvoice ? Math.min(currentInvoice.monto, Number(currentInvoice.saldo_pendiente) + restoredAmount) : fd.saldoAnterior || fd.saldoNuevo,
                estado: 'pendiente'
              };
            });
            await supabaseAdmin.from('visits').update({
              cobro: {
                ...cobro,
                facturasDetalle: updatedDetalle
              }
            }).eq('id', visit.id);
          }
        }
      } catch (propagateErr) {
        console.error('Error propagando anulación NC a visitas:', propagateErr);
      }
      // 4. Delete the nc_application record
      await supabaseAdmin.from('nc_applications').delete().eq('id', ncApp.id);
      // 5. Refresh data
      await loadInvoices();
      await loadCreditNotes();
      await loadNCApplications();
      addActivityLog('Anuló aplicación NC', `NC: ${ncApp.credit_note_numero} | Factura: ${ncApp.invoice_numero} | Farmacia: ${ncApp.farmacia}`);
      setAnularNCApp(null);
    } catch (err) {
      console.error('Error anulando aplicación NC:', err);
    } finally {
      setIsAnulandoNC(false);
    }
  };
  useEffect(() => {
    loadInvoices();
    loadCreditNotes();
    loadNCApplications();
  }, []);
  // Auto-refresh when invoices or credit notes change in Supabase
  const refreshAllData = useCallback(() => {
    loadInvoices();
    loadCreditNotes();
    loadNCApplications();
  }, []);
  useRealtimeRefresh(['invoices', 'credit_notes', 'nc_applications', 'app_settings'], refreshAllData);
  // Invoice filtering
  // Admin and users with cuentas_cobrar permission see all invoices
  const canSeeAllInvoices = isAdmin || user?.permisos?.cuentas_cobrar === true;
  const filteredInvoices = invoices.filter(inv => {
    if (!canSeeAllInvoices && inv.vendedorId !== user?.id) return false;
    const matchesSearch = inv.farmacia.toLowerCase().includes(searchQuery.toLowerCase()) || inv.numeroFactura.toLowerCase().includes(searchQuery.toLowerCase()) || inv.vendedor.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inv.estado === statusFilter;
    const matchesVendedor = vendedorFilter === 'all' || inv.vendedor === vendedorFilter;
    const invDate = new Date(inv.fechaEmision).toLocaleDateString('en-CA', {
      timeZone: 'America/Santo_Domingo'
    });
    const matchesDateFrom = !dateFrom || invDate >= dateFrom;
    const matchesDateTo = !dateTo || invDate <= dateTo;
    return matchesSearch && matchesStatus && matchesVendedor && matchesDateFrom && matchesDateTo;
  });
  const {
    items: sortedInvoices,
    requestSort,
    sortConfig
  } = useSortableData(filteredInvoices, {
    numeroFactura: i => i.numeroFactura,
    farmacia: i => i.farmacia,
    vendedor: i => i.vendedor,
    fechaVencimiento: i => new Date(i.fechaVencimiento).getTime(),
    subTotal: i => i.subTotal,
    itbis: i => i.itbis,
    monto: i => i.monto,
    saldoPendiente: i => i.saldoPendiente,
    estado: i => i.estado
  });
  const finalInvoices = sortConfig ? sortedInvoices : [...sortedInvoices].sort((a, b) => new Date(b.fechaEmision).getTime() - new Date(a.fechaEmision).getTime());
  // ── Global totals (unfiltered) — always show the REAL numbers ──────────────
  const allVisibleInvoices = invoices.filter(inv => {
    if (!canSeeAllInvoices && inv.vendedorId !== user?.id) return false;
    return true;
  });
  const totalPendiente = allVisibleInvoices.reduce((sum, inv) => sum + inv.saldoPendiente, 0);
  const totalVencido = allVisibleInvoices.filter(inv => inv.estado === 'vencida').reduce((sum, inv) => sum + inv.saldoPendiente, 0);
  const facturasPendientes = allVisibleInvoices.filter(inv => inv.saldoPendiente > 0).length;
  // Filtered count for table info
  const filteredCount = finalInvoices.length;
  const isFiltered = searchQuery || statusFilter !== 'all' || vendedorFilter !== 'all' || dateFrom || dateTo;
  const totalPages = Math.ceil(finalInvoices.length / ITEMS_PER_PAGE);
  const paginatedInvoices = finalInvoices.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // Credit Notes filtering
  const filteredNC = creditNotes.filter(nc => {
    if (!canSeeAllInvoices && nc.vendedorId !== user?.id) return false;
    return nc.farmacia.toLowerCase().includes(ncSearchQuery.toLowerCase()) || nc.numero.toLowerCase().includes(ncSearchQuery.toLowerCase());
  });
  const ncTotalPages = Math.ceil(filteredNC.length / ITEMS_PER_PAGE);
  const paginatedNC = filteredNC.slice((ncPage - 1) * ITEMS_PER_PAGE, ncPage * ITEMS_PER_PAGE);
  const ncDisponibles = filteredNC.filter(nc => nc.estado === 'disponible');
  const ncAplicadas = filteredNC.filter(nc => nc.estado === 'aplicada');
  const ncMontoDisponible = ncDisponibles.reduce((sum, nc) => sum + nc.montoDisponible, 0);
  // Application tab data
  const appInvoices = invoices.filter(inv => farmaciaMatch(inv.farmacia, appFarmacia) && inv.saldoPendiente > 0 && inv.estado !== 'pagada');
  const appCreditNotes = creditNotes.filter(nc => farmaciaMatch(nc.farmacia, appFarmacia) && (nc.estado === 'disponible' || nc.estado === 'parcial') && nc.montoDisponible > 0);
  const selectedInvoice = appInvoices.find(inv => inv.id === selectedInvoiceId) || null;
  const selectedNCs = appCreditNotes.filter(nc => selectedNCIds.includes(nc.id));
  // Calculation logic (BR-03 to BR-07)
  // When multiple NCs are applied, use the CURRENT saldo to derive remaining subtotal
  const calculateApplication = () => {
    if (!selectedInvoice) return null;
    const subtotalOriginal = selectedInvoice.subTotal;
    const itbisOriginal = selectedInvoice.itbis;
    const totalOriginal = selectedInvoice.monto;
    // If saldo < monto, previous NCs have already reduced the subtotal
    // Remaining subtotal = saldoPendiente - itbis (ITBIS never changes)
    const currentSaldo = selectedInvoice.saldoPendiente;
    const remainingSubtotal = currentSaldo < totalOriginal ? Math.max(0, currentSaldo - itbisOriginal) : subtotalOriginal;
    // Sum all selected NCs, capped at remaining subtotal
    let ncMontoTotal = 0;
    const ncBreakdown: {
      id: string;
      numero: string;
      montoAplicado: number;
      montoDisponible: number;
    }[] = [];
    let subtotalLeft = remainingSubtotal;
    for (const nc of selectedNCs) {
      const aplicar = Math.min(nc.montoDisponible, Math.max(0, subtotalLeft));
      ncBreakdown.push({
        id: nc.id,
        numero: nc.numero,
        montoAplicado: aplicar,
        montoDisponible: nc.montoDisponible
      });
      ncMontoTotal += aplicar;
      subtotalLeft -= aplicar;
    }
    const subtotalAjustado = Math.max(0, remainingSubtotal - ncMontoTotal);
    // No discount applied in this section — only credit note
    const descuentoMonto = 0;
    const subtotalFinal = subtotalAjustado;
    const itbisFinal = itbisOriginal; // BR-05: ITBIS unchanged
    const totalAPagar = subtotalFinal + itbisFinal; // BR-06
    return {
      subtotalOriginal,
      itbisOriginal,
      totalOriginal,
      ncMonto: ncMontoTotal,
      ncBreakdown,
      subtotalAjustado,
      descuentoPct: 0,
      descuentoMonto,
      subtotalFinal,
      itbisFinal,
      totalAPagar,
      remainingSubtotal
    };
  };
  const calculation = calculateApplication();
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2
  }).format(amount);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const getStatusBadge = (status: Invoice['estado']) => {
    switch (status) {
      case 'pagada':
        return <Badge variant="success" data-id="element-1714">Pagada</Badge>;
      case 'parcial':
        return <Badge variant="warning" data-id="element-1715">Parcial</Badge>;
      case 'vencida':
        return <Badge variant="danger" data-id="element-1716">Vencida</Badge>;
      default:
        return <Badge variant="info" data-id="element-1717">Pendiente</Badge>;
    }
  };
  const getNCStatusBadge = (status: CreditNote['estado']) => {
    switch (status) {
      case 'disponible':
        return <Badge variant="success" data-id="element-1718">Disponible</Badge>;
      case 'aplicada':
        return <Badge variant="info" data-id="element-1719">Aplicada</Badge>;
      case 'parcial':
        return <Badge variant="warning" data-id="element-1720">Parcial</Badge>;
      default:
        return <Badge variant="default" data-id="element-1721">{status}</Badge>;
    }
  };
  // Invoice CRUD
  const isVendedor = user?.permisos?.['new-visit'] === true;
  const openAddModal = () => {
    setEditingInvoice(null);
    setFacturaError('');
    setFormData({
      ...emptyForm,
      numeroFactura: '',
      farmacia: FARMACIAS[0] || '',
      vendedor: isVendedor ? user?.nombre || '' : '',
      vendedorId: isVendedor ? user?.id || '' : ''
    });
    setIsModalOpen(true);
  };
  const openEditModal = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      numeroFactura: invoice.numeroFactura,
      farmacia: invoice.farmacia,
      vendedor: invoice.vendedor,
      vendedorId: invoice.vendedorId || '',
      fechaEmision: new Date(invoice.fechaEmision).toISOString().split('T')[0],
      fechaVencimiento: new Date(invoice.fechaVencimiento).toISOString().split('T')[0],
      subTotal: String(invoice.subTotal || 0),
      itbis: String(invoice.itbis || 0),
      monto: String(invoice.monto),
      saldoPendiente: String(invoice.saldoPendiente),
      estado: invoice.estado,
      ppPct: invoice.ppPct != null ? String(invoice.ppPct) : ''
    });
    setFacturaError('');
    setIsModalOpen(true);
  };
  const handleSave = async () => {
    if (!formData.numeroFactura || !formData.monto || !formData.fechaVencimiento) return;
    const isDuplicate = invoices.some(inv => inv.numeroFactura.trim().toLowerCase() === formData.numeroFactura.trim().toLowerCase() && inv.id !== editingInvoice?.id);
    if (isDuplicate) {
      setFacturaError(`El número de factura "${formData.numeroFactura}" ya existe.`);
      return;
    }
    setFacturaError('');
    setIsSaving(true);
    try {
      let finalEstado = formData.estado;
      const fechaVenc = new Date(formData.fechaVencimiento);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const saldoPend = Number(formData.saldoPendiente);
      if (finalEstado !== 'pagada' && fechaVenc < today && saldoPend > 0) finalEstado = 'vencida';
      if (finalEstado === 'vencida' && fechaVenc >= today) finalEstado = saldoPend < Number(formData.monto) && saldoPend > 0 ? 'parcial' : 'pendiente';
      const payload = {
        numero_factura: formData.numeroFactura,
        farmacia: formData.farmacia,
        vendedor: formData.vendedor,
        vendedor_id: formData.vendedorId || null,
        fecha_emision: new Date(formData.fechaEmision).toISOString(),
        fecha_vencimiento: new Date(formData.fechaVencimiento).toISOString(),
        sub_total: Number(formData.subTotal) || 0,
        itbis: Number(formData.itbis) || 0,
        monto: Number(formData.monto),
        saldo_pendiente: saldoPend,
        estado: finalEstado,
        pp_pct: formData.ppPct !== '' ? Number(formData.ppPct) : null
      };
      if (editingInvoice) {
        const {
          data
        } = await supabaseAdmin.from('invoices').update(payload).eq('id', editingInvoice.id).select().single();
        if (data) setInvoices(prev => prev.map(inv => inv.id === editingInvoice.id ? mapInvoice(data) : inv));
        addActivityLog('Editó factura', `Factura: ${formData.numeroFactura} | Farmacia: ${formData.farmacia} | Monto: RD${formData.monto}`);
        // ── Propagate invoice changes to related visits ──────────────────────
        // Find visits that reference this invoice in their cobro.facturasDetalle
        // so VisitsPage, ReportsPage, and CollectionsPage show updated data.
        try {
          const {
            data: relatedVisits
          } = await supabaseAdmin.from('visits').select('id, cobro').eq('cliente_nombre', editingInvoice.farmacia).not('cobro', 'is', null).limit(500);
          if (relatedVisits && relatedVisits.length > 0) {
            const oldNumero = editingInvoice.numeroFactura;
            const newNumero = formData.numeroFactura;
            for (const visit of relatedVisits) {
              const cobro = visit.cobro as any;
              if (!cobro?.facturasDetalle || !Array.isArray(cobro.facturasDetalle)) continue;
              // Check if this visit references the edited invoice
              const matchIdx = cobro.facturasDetalle.findIndex((fd: any) => fd.facturaId === editingInvoice.id || fd.numeroFactura && fd.numeroFactura === oldNumero);
              if (matchIdx === -1) continue;
              // Update the matching facturasDetalle entry
              const updatedDetalle = cobro.facturasDetalle.map((fd: any, idx: number) => {
                if (idx !== matchIdx) return fd;
                return {
                  ...fd,
                  numeroFactura: newNumero,
                  saldoNuevo: saldoPend,
                  estado: finalEstado,
                  subTotal: Number(formData.subTotal) || fd.subTotal || 0,
                  itbis: Number(formData.itbis) || fd.itbis || 0
                };
              });
              // Update cobro.numeroFactura string if invoice number changed
              let updatedNumeroFactura = cobro.numeroFactura || '';
              if (oldNumero !== newNumero && updatedNumeroFactura.includes(oldNumero)) {
                updatedNumeroFactura = updatedNumeroFactura.split(',').map((s: string) => s.trim() === oldNumero ? newNumero : s.trim()).join(', ');
              }
              await supabaseAdmin.from('visits').update({
                cobro: {
                  ...cobro,
                  facturasDetalle: updatedDetalle,
                  numeroFactura: updatedNumeroFactura
                }
              }).eq('id', visit.id);
            }
          }
        } catch (propagateErr) {
          console.error('Error propagando cambios a visitas:', propagateErr);
          // Don't block the save — invoice was already saved successfully
        }
      } else {
        const {
          data
        } = await supabaseAdmin.from('invoices').insert(payload).select().single();
        if (data) setInvoices(prev => [mapInvoice(data), ...prev]);
        addActivityLog('Creó factura', `Factura: ${formData.numeroFactura} | Farmacia: ${formData.farmacia} | Monto: RD${formData.monto}`);
      }
      setIsModalOpen(false);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!editingInvoice) return;
    addActivityLog('Eliminó factura', `Factura: ${editingInvoice.numeroFactura} | Farmacia: ${editingInvoice.farmacia}`);
    await supabaseAdmin.from('invoices').delete().eq('id', editingInvoice.id);
    setInvoices(prev => prev.filter(inv => inv.id !== editingInvoice.id));
    // ── Propagate deletion to related visits ─────────────────────────────
    try {
      const {
        data: relatedVisits
      } = await supabaseAdmin.from('visits').select('id, cobro').eq('cliente_nombre', editingInvoice.farmacia).not('cobro', 'is', null).limit(500);
      if (relatedVisits && relatedVisits.length > 0) {
        for (const visit of relatedVisits) {
          const cobro = visit.cobro as any;
          if (!cobro?.facturasDetalle || !Array.isArray(cobro.facturasDetalle)) continue;
          const hadMatch = cobro.facturasDetalle.some((fd: any) => fd.facturaId === editingInvoice.id || fd.numeroFactura && fd.numeroFactura === editingInvoice.numeroFactura);
          if (!hadMatch) continue;
          // Remove the deleted invoice from facturasDetalle
          const updatedDetalle = cobro.facturasDetalle.filter((fd: any) => fd.facturaId !== editingInvoice.id && fd.numeroFactura !== editingInvoice.numeroFactura);
          // Remove from cobro.numeroFactura string
          let updatedNumeroFactura = cobro.numeroFactura || '';
          if (updatedNumeroFactura.includes(editingInvoice.numeroFactura)) {
            updatedNumeroFactura = updatedNumeroFactura.split(',').map((s: string) => s.trim()).filter((s: string) => s !== editingInvoice.numeroFactura).join(', ');
          }
          await supabaseAdmin.from('visits').update({
            cobro: {
              ...cobro,
              facturasDetalle: updatedDetalle,
              numeroFactura: updatedNumeroFactura
            }
          }).eq('id', visit.id);
        }
      }
    } catch (err) {
      console.error('Error propagando eliminación a visitas:', err);
    }
    setIsDeleteModalOpen(false);
    setIsModalOpen(false);
  };
  // Credit Note CRUD
  const openAddNCModal = () => {
    setEditingNC(null);
    setNCError('');
    const existing = creditNotes.map(nc => {
      const match = nc.numero.match(/^NC-(\d+)$/i);
      return match ? parseInt(match[1], 10) : 0;
    }).filter(n => n > 0);
    const nextNum = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    const nextNC = `NC-${String(nextNum).padStart(5, '0')}`;
    setNCFormData({
      ...emptyNCForm,
      numero: nextNC,
      farmacia: FARMACIAS[0] || '',
      vendedor: isVendedor ? user?.nombre || '' : '',
      vendedorId: isVendedor ? user?.id || '' : ''
    });
    setIsNCModalOpen(true);
  };
  const openEditNCModal = (nc: CreditNote) => {
    setEditingNC(nc);
    setNCFormData({
      numero: nc.numero,
      farmacia: nc.farmacia,
      vendedor: nc.vendedor,
      vendedorId: nc.vendedorId || '',
      monto: String(nc.monto),
      notas: nc.notas || ''
    });
    setNCError('');
    setIsNCModalOpen(true);
  };
  const handleSaveNC = async () => {
    if (!ncFormData.numero || !ncFormData.monto || !ncFormData.farmacia) return;
    const isDuplicate = creditNotes.some(nc => nc.numero.trim().toLowerCase() === ncFormData.numero.trim().toLowerCase() && nc.id !== editingNC?.id);
    if (isDuplicate) {
      setNCError(`El número de NC "${ncFormData.numero}" ya existe.`);
      return;
    }
    setNCError('');
    setIsSaving(true);
    try {
      const monto = Number(ncFormData.monto) || 0;
      const payload = {
        numero: ncFormData.numero,
        farmacia: ncFormData.farmacia,
        vendedor: ncFormData.vendedor,
        vendedor_id: ncFormData.vendedorId || null,
        monto,
        monto_disponible: editingNC ? editingNC.montoDisponible : monto,
        estado: 'disponible',
        notas: ncFormData.notas
      };
      if (editingNC) {
        const {
          data
        } = await supabaseAdmin.from('credit_notes').update(payload).eq('id', editingNC.id).select().single();
        if (data) setCreditNotes(prev => prev.map(nc => nc.id === editingNC.id ? mapCreditNote(data) : nc));
      } else {
        const {
          data
        } = await supabaseAdmin.from('credit_notes').insert(payload).select().single();
        if (data) setCreditNotes(prev => [mapCreditNote(data), ...prev]);
      }
      addActivityLog(editingNC ? 'Editó nota de crédito' : 'Creó nota de crédito', `NC: ${ncFormData.numero} | Farmacia: ${ncFormData.farmacia} | Monto: RD${ncFormData.monto}`);
      setIsNCModalOpen(false);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDeleteNC = async () => {
    if (!editingNC) return;
    addActivityLog('Eliminó nota de crédito', `NC: ${editingNC.numero} | Farmacia: ${editingNC.farmacia}`);
    await supabaseAdmin.from('credit_notes').delete().eq('id', editingNC.id);
    setCreditNotes(prev => prev.filter(nc => nc.id !== editingNC.id));
    setIsNCDeleteModalOpen(false);
    setIsNCModalOpen(false);
  };
  // Apply NC to Invoice
  const handleApplyNC = async () => {
    if (!selectedInvoice || !calculation) return;
    setIsApplying(true);
    try {
      // Record application for each selected NC (only those with monto > 0)
      for (const ncItem of calculation.ncBreakdown) {
        if (ncItem.montoAplicado <= 0) continue;
        const nc = selectedNCs.find(n => n.id === ncItem.id);
        if (!nc) continue;
        const {
          error: ncAppError
        } = await supabaseAdmin.from('nc_applications').insert({
          credit_note_id: nc.id,
          credit_note_numero: nc.numero,
          invoice_id: selectedInvoice.id,
          invoice_numero: selectedInvoice.numeroFactura,
          farmacia: selectedInvoice.farmacia,
          subtotal_original: calculation.subtotalOriginal,
          itbis_original: calculation.itbisOriginal,
          total_original: calculation.totalOriginal,
          nc_monto_aplicado: ncItem.montoAplicado,
          subtotal_ajustado: calculation.subtotalAjustado,
          descuento_pct: calculation.descuentoPct,
          descuento_monto: calculation.descuentoMonto,
          subtotal_final: calculation.subtotalFinal,
          itbis_final: calculation.itbisFinal,
          total_a_pagar: calculation.totalAPagar,
          aplicado_por: user?.nombre || 'Sistema'
        });
        if (ncAppError) {
          console.error('Error guardando nc_applications:', ncAppError.message);
        }
      }
      // Update invoice balance — totalAPagar is already based on current saldo
      const newBalance = Math.max(0, calculation.totalAPagar);
      const newStatus = newBalance <= 0 ? 'pagada' : newBalance < selectedInvoice.monto ? 'parcial' : 'pendiente';
      await supabaseAdmin.from('invoices').update({
        saldo_pendiente: newBalance,
        estado: newStatus
      }).eq('id', selectedInvoice.id);
      // Update each credit note used
      for (const ncItem of calculation.ncBreakdown) {
        const nc = selectedNCs.find(n => n.id === ncItem.id);
        if (!nc || ncItem.montoAplicado <= 0) continue;
        const newNCBalance = nc.montoDisponible - ncItem.montoAplicado;
        const newNCStatus = newNCBalance <= 0 ? 'aplicada' : 'parcial';
        await supabaseAdmin.from('credit_notes').update({
          monto_disponible: Math.max(0, newNCBalance),
          estado: newNCStatus,
          factura_aplicada: selectedInvoice.numeroFactura
        }).eq('id', nc.id);
      }
      // ── Propagate NC application changes to related visits ─────────────
      try {
        const newSaldo = newBalance;
        const {
          data: relatedVisits
        } = await supabaseAdmin.from('visits').select('id, cobro').eq('cliente_nombre', selectedInvoice.farmacia).not('cobro', 'is', null).limit(500);
        if (relatedVisits && relatedVisits.length > 0) {
          for (const visit of relatedVisits) {
            const cobro = visit.cobro as any;
            if (!cobro?.facturasDetalle || !Array.isArray(cobro.facturasDetalle)) continue;
            const matchIdx = cobro.facturasDetalle.findIndex((fd: any) => fd.facturaId === selectedInvoice.id || fd.numeroFactura && fd.numeroFactura === selectedInvoice.numeroFactura);
            if (matchIdx === -1) continue;
            const updatedDetalle = cobro.facturasDetalle.map((fd: any, idx: number) => {
              if (idx !== matchIdx) return fd;
              return {
                ...fd,
                saldoNuevo: newSaldo,
                estado: newStatus
              };
            });
            await supabaseAdmin.from('visits').update({
              cobro: {
                ...cobro,
                facturasDetalle: updatedDetalle
              }
            }).eq('id', visit.id);
          }
        }
      } catch (propagateErr) {
        console.error('Error propagando NC a visitas:', propagateErr);
      }
      await loadInvoices();
      await loadCreditNotes();
      const ncNames = calculation.ncBreakdown.map(n => n.numero).join(', ') || 'N/A';
      addActivityLog('Aplicó nota(s) de crédito', `NC: ${ncNames} → Factura: ${selectedInvoice.numeroFactura} | Farmacia: ${selectedInvoice.farmacia} | Monto total NC: RD${calculation.ncMonto.toFixed(2)}`);
      setApplicationSuccess(true);
      setSelectedInvoiceId(null);
      setSelectedNCIds([]);
    } finally {
      setIsApplying(false);
    }
  };
  // Payment modal
  const openPaymentModal = () => {
    setPaymentClient('');
    setPaymentAmounts({});
    setPaymentMetodo('efectivo');
    setPaymentDone(false);
    setLastPaymentData(null);
    setIsPaymentModalOpen(true);
  };
  const clientInvoices = invoices.filter(inv => farmaciaMatch(inv.farmacia, paymentClient) && inv.saldoPendiente > 0 && inv.estado !== 'pagada');
  const handlePaymentAmountChange = (invoiceId: string, amount: string) => {
    const val = parseFloat(amount);
    if (isNaN(val) || val < 0) {
      const next = {
        ...paymentAmounts
      };
      delete next[invoiceId];
      setPaymentAmounts(next);
      return;
    }
    setPaymentAmounts(prev => ({
      ...prev,
      [invoiceId]: val
    }));
  };
  const totalPayment = Object.values(paymentAmounts).reduce((a, b) => a + b, 0);
  const handleProcessPayment = async () => {
    if (totalPayment <= 0) return;
    setIsSaving(true);
    try {
      const updates = Object.entries(paymentAmounts).map(async ([invoiceId, amount]) => {
        const invoice = invoices.find(i => i.id === invoiceId);
        if (!invoice) return;
        const newBalance = Math.max(0, invoice.saldoPendiente - amount);
        const newStatus = newBalance <= 0 ? 'pagada' : newBalance < invoice.monto ? 'parcial' : 'pendiente';
        const {
          data
        } = await supabaseAdmin.from('invoices').update({
          saldo_pendiente: newBalance,
          estado: newStatus
        }).eq('id', invoiceId).select().single();
        return data ? mapInvoice(data) : null;
      });
      const results = await Promise.all(updates);
      const updatedInvoices = results.filter(Boolean) as Invoice[];
      setInvoices(prev => prev.map(inv => {
        const u = updatedInvoices.find(u => u.id === inv.id);
        return u || inv;
      }));
      const paidInvoices = Object.entries(paymentAmounts).filter(([, amt]) => amt > 0).map(([invoiceId, abono]) => {
        const inv = invoices.find(i => i.id === invoiceId);
        return {
          numeroFactura: inv?.numeroFactura || '',
          monto: inv?.monto || 0,
          abono
        };
      });
      // ── Propagate payment changes to related visits ────────────────────
      try {
        const {
          data: relatedVisits
        } = await supabaseAdmin.from('visits').select('id, cobro').eq('cliente_nombre', paymentClient).not('cobro', 'is', null).limit(500);
        if (relatedVisits && relatedVisits.length > 0) {
          for (const visit of relatedVisits) {
            const cobro = visit.cobro as any;
            if (!cobro?.facturasDetalle || !Array.isArray(cobro.facturasDetalle)) continue;
            let changed = false;
            const updatedDetalle = cobro.facturasDetalle.map((fd: any) => {
              // Find if this factura was part of the payment
              const matchedUpdate = updatedInvoices.find(u => u.id === fd.facturaId || fd.numeroFactura && u.numeroFactura === fd.numeroFactura);
              if (!matchedUpdate) return fd;
              changed = true;
              return {
                ...fd,
                saldoNuevo: matchedUpdate.saldoPendiente,
                estado: matchedUpdate.estado
              };
            });
            if (!changed) continue;
            await supabaseAdmin.from('visits').update({
              cobro: {
                ...cobro,
                facturasDetalle: updatedDetalle
              }
            }).eq('id', visit.id);
          }
        }
      } catch (propagateErr) {
        console.error('Error propagando pago a visitas:', propagateErr);
      }
      setLastPaymentData({
        farmacia: paymentClient,
        invoices: paidInvoices,
        total: totalPayment
      });
      addActivityLog('Registró pago múltiple', `Farmacia: ${paymentClient} | Total: RD${totalPayment.toFixed(2)} | Facturas: ${paidInvoices.map(i => i.numeroFactura).join(', ')}`);
      setPaymentDone(true);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDownloadRecibo = async () => {
    if (!lastPaymentData) return;
    setIsDownloadingRecibo(true);
    try {
      await downloadReciboPago(lastPaymentData.farmacia, lastPaymentData.invoices, lastPaymentData.total, paymentMetodo);
    } finally {
      setIsDownloadingRecibo(false);
    }
  };
  const handleDownloadPDF = async () => {
    if (!lastPaymentData) return;
    setIsDownloadingRecibo(true);
    try {
      await downloadReciboPago(lastPaymentData.farmacia, lastPaymentData.invoices, lastPaymentData.total, paymentMetodo);
    } finally {
      setIsDownloadingRecibo(false);
    }
  };
  const generateEstadoCuenta = async () => {
    if (!estadoCuentaClient) return;
    setIsGeneratingEstadoCuenta(true);
    try {
      // 1. Filter invoices for the selected client and date range
      const clientInvs = invoices.filter(inv => {
        if (!farmaciaMatch(inv.farmacia, estadoCuentaClient)) return false;
        const invDate = new Date(inv.fechaEmision).toLocaleDateString('en-CA', {
          timeZone: 'America/Santo_Domingo'
        });
        if (dateFrom && invDate < dateFrom) return false;
        if (dateTo && invDate > dateTo) return false;
        return true;
      }).sort((a, b) => new Date(b.fechaEmision).getTime() - new Date(a.fechaEmision).getTime());
      // 2. Create PDF
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();
      const ml = 15;
      const mr = 15;
      // 3. Header band (dark navy)
      const genDate = new Date().toLocaleDateString('es-DO', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
      const headerH = 36;
      doc.setFillColor(13, 27, 70);
      doc.rect(0, 0, pw, headerH, 'F');
      // Logo white rounded box
      const logoBase64 = await getLogoBase64();
      if (logoBase64) {
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(ml, 5, 26, 26, 3, 3, 'F');
        doc.addImage(logoBase64, 'PNG', ml + 1, 6, 24, 24);
      }
      // Company name
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('Miladys Rodriguez & Asoc.', ml + 30, 16);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(123, 163, 216);
      doc.text('Sistema de Control de Vendedores', ml + 30, 23);
      // Date top right
      doc.setFontSize(8);
      doc.setTextColor(123, 163, 216);
      doc.text('Generado: ' + genDate, pw - mr, 16, {
        align: 'right'
      });
      // Accent bar
      doc.setFillColor(41, 82, 196);
      doc.rect(0, headerH, pw, 2, 'F');
      // Title section
      let y = headerH + 2;
      const hasDateRange = dateFrom || dateTo;
      const titleStripH = hasDateRange ? 30 : 24;
      doc.setFillColor(248, 250, 255);
      doc.rect(0, y, pw, titleStripH, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(0, y + titleStripH, pw, y + titleStripH);
      y += 6;
      doc.setTextColor(13, 27, 70);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('ESTADO DE CUENTA', ml, y + 5);
      // Records pill (top right)
      const pillW = 30;
      doc.setFillColor(41, 82, 196);
      doc.roundedRect(pw - mr - pillW, y, pillW, 8, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.text(clientInvs.length + ' facturas', pw - mr - pillW / 2, y + 5.5, {
        align: 'center'
      });
      // Date range box
      if (hasDateRange) {
        const boxY = y + 10;
        const boxH = 10;
        const boxW = pw - ml - mr;
        doc.setFillColor(234, 240, 255);
        doc.setDrawColor(41, 82, 196);
        doc.setLineWidth(0.4);
        doc.roundedRect(ml, boxY, boxW, boxH, 2, 2, 'FD');
        doc.setFillColor(41, 82, 196);
        doc.roundedRect(ml + 2, boxY + 2, 6, 6, 1, 1, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.text('F', ml + 5, boxY + 6.2, {
          align: 'center'
        });
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(41, 82, 196);
        const periodLabel = 'Período:  ';
        doc.text(periodLabel, ml + 10, boxY + 6.5);
        const periodLabelW = doc.getTextWidth(periodLabel);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(13, 27, 70);
        const periodText = `${dateFrom ? formatDate(new Date(dateFrom + 'T00:00:00')) : 'Inicio'}  al  ${dateTo ? formatDate(new Date(dateTo + 'T00:00:00')) : 'Hoy'}`;
        doc.text(periodText, ml + 10 + periodLabelW, boxY + 6.5);
      }
      y = headerH + 2 + titleStripH + 2;
      // Client info row
      doc.setFillColor(255, 255, 255);
      doc.rect(ml, y, pw - ml - mr, 10, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.2);
      doc.rect(ml, y, pw - ml - mr, 10, 'S');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(41, 82, 196);
      doc.text('Cliente:', ml + 3, y + 6.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(13, 27, 70);
      doc.text(estadoCuentaClient, ml + 18, y + 6.5);
      y += 14;
      // Table
      const cols = ['Factura', 'Emisión', 'Vencimiento', 'Monto', 'Saldo Pendiente', 'Estado'];
      const colW = [30, 25, 25, 35, 35, 30];
      const tableW = colW.reduce((a, b) => a + b, 0);
      doc.setFillColor(13, 27, 70);
      doc.rect(ml, y, tableW, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      let x = ml;
      cols.forEach((col, i) => {
        doc.text(col, x + colW[i] / 2, y + 5.5, {
          align: 'center'
        });
        x += colW[i];
      });
      y += 8;
      let totalMonto = 0;
      let totalSaldo = 0;
      clientInvs.forEach((inv, i) => {
        if (y > ph - 30) {
          doc.addPage();
          y = 15;
          // Draw header again
          doc.setFillColor(13, 27, 70);
          doc.rect(ml, y, tableW, 8, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          let hx = ml;
          cols.forEach((col, idx) => {
            doc.text(col, hx + colW[idx] / 2, y + 5.5, {
              align: 'center'
            });
            hx += colW[idx];
          });
          y += 8;
        }
        totalMonto += inv.monto;
        totalSaldo += inv.saldoPendiente;
        const isEven = i % 2 === 0;
        doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 255);
        doc.rect(ml, y, tableW, 7, 'F');
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.2);
        doc.rect(ml, y, tableW, 7, 'S');
        doc.setTextColor(51, 65, 85);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        let cx = ml;
        // Factura
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 23, 42);
        doc.text(inv.numeroFactura, cx + colW[0] / 2, y + 4.5, {
          align: 'center'
        });
        cx += colW[0];
        // Emision
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(51, 65, 85);
        doc.text(formatDate(inv.fechaEmision), cx + colW[1] / 2, y + 4.5, {
          align: 'center'
        });
        cx += colW[1];
        // Vencimiento
        doc.text(formatDate(inv.fechaVencimiento), cx + colW[2] / 2, y + 4.5, {
          align: 'center'
        });
        cx += colW[2];
        // Monto
        doc.text(formatCurrency(inv.monto), cx + colW[3] / 2, y + 4.5, {
          align: 'center'
        });
        cx += colW[3];
        // Saldo
        doc.setFont('helvetica', 'bold');
        doc.text(formatCurrency(inv.saldoPendiente), cx + colW[4] / 2, y + 4.5, {
          align: 'center'
        });
        cx += colW[4];
        // Estado
        const estadoStr = inv.estado.charAt(0).toUpperCase() + inv.estado.slice(1);
        if (inv.estado === 'pagada') doc.setTextColor(5, 150, 105);else if (inv.estado === 'vencida') doc.setTextColor(220, 38, 38);else if (inv.estado === 'parcial') doc.setTextColor(217, 119, 6);else doc.setTextColor(30, 41, 59);
        doc.text(estadoStr, cx + colW[5] / 2, y + 4.5, {
          align: 'center'
        });
        y += 7;
      });
      // Summary row
      if (y > ph - 40) {
        doc.addPage();
        y = 15;
      }
      y += 2;
      doc.setFillColor(248, 250, 252);
      doc.rect(ml, y, tableW, 8, 'F');
      doc.setDrawColor(180, 200, 220);
      doc.setLineWidth(0.3);
      doc.rect(ml, y, tableW, 8, 'S');
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(`Total Facturas: ${clientInvs.length}`, ml + 5, y + 5.5);
      let sumX = ml + colW[0] + colW[1] + colW[2];
      doc.text(formatCurrency(totalMonto), sumX + colW[3] / 2, y + 5.5, {
        align: 'center'
      });
      sumX += colW[3];
      doc.text(formatCurrency(totalSaldo), sumX + colW[4] / 2, y + 5.5, {
        align: 'center'
      });
      // Footer
      const totalPages = (doc as any).internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFillColor(13, 27, 70);
        doc.rect(0, ph - 9, pw, 9, 'F');
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(123, 163, 216);
        doc.text('Miladys Rodriguez & Asoc. © 2025 — Sistema de Control de Vendedores', pw / 2, ph - 3.5, {
          align: 'center'
        });
        doc.text(`Página ${i} de ${totalPages}`, pw - mr, ph - 3.5, {
          align: 'right'
        });
      }
      doc.save(`estado-cuenta-${estadoCuentaClient.replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`);
      addActivityLog('Generó estado de cuenta', `Cliente: ${estadoCuentaClient} | Facturas: ${clientInvs.length}`);
      setShowEstadoCuenta(false);
    } catch (err) {
      console.error('Error generating estado de cuenta:', err);
      alert('Error al generar el estado de cuenta.');
    } finally {
      setIsGeneratingEstadoCuenta(false);
    }
  };
  return <div className="space-y-6" data-id="element-1722">
      <PageHeader title="Cuentas por Cobrar" subtitle="Gestión de facturas, notas de crédito y cobros" icon={<DollarSignIcon className="w-6 h-6" data-id="element-1724" />} data-id="element-1723">
        <div className="flex flex-wrap gap-2" data-id="element-1725">
          {canSeeDiscount && <Button variant="secondary" leftIcon={<SettingsIcon className="w-4 h-4" data-id="element-1727" />} onClick={() => {
          setDiscountConfigDraft({
            pct: discountPct,
            days: discountDays
          });
          setIsDiscountConfigOpen(true);
        }} data-id="element-1726">
              Config. Descuento
            </Button>}
          <Button variant="secondary" leftIcon={<CreditCardIcon className="w-4 h-4" data-id="element-1729" />} onClick={openPaymentModal} data-id="element-1728">
            Registrar Pago
          </Button>
          <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1731" />} onClick={openAddModal} data-id="element-1730">
            Nueva Factura
          </Button>
        </div>
      </PageHeader>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit" data-id="element-1732">
        <button onClick={() => setActiveTab('facturas')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${activeTab === 'facturas' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1733">
          <FileTextIcon className="w-4 h-4" data-id="element-1734" />
          Facturas
        </button>
        <button onClick={() => setActiveTab('notas-credito')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${activeTab === 'notas-credito' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1735">
          <FileMinusIcon className="w-4 h-4" data-id="element-1736" />
          Notas de Crédito
        </button>
        <button onClick={() => {
        setActiveTab('aplicar-nc');
        setApplicationSuccess(false);
      }} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${activeTab === 'aplicar-nc' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1737">
          <ArrowRightLeftIcon className="w-4 h-4" data-id="element-1738" />
          Aplicar NC
        </button>
        <button onClick={() => setActiveTab('historial-nc')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${activeTab === 'historial-nc' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1739">
          <ClockIcon className="w-4 h-4" data-id="element-1740" />
          Historial NC
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: FACTURAS */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'facturas' && <>
          {canSeeDiscount && <div className="flex items-center gap-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl" data-id="element-1741">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1742">
                <PercentIcon className="w-5 h-5 text-emerald-600" data-id="element-1743" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-1744">
                <p className="text-sm font-semibold text-slate-900" data-id="element-1745">
                  Política de Descuento por Pronto Pago
                </p>
                <p className="text-xs text-slate-600 mt-0.5" data-id="element-1746">
                  <strong className="text-emerald-700" data-id="element-1747">
                    {discountPct}% de descuento
                  </strong>{' '}
                  para cobros dentro de los primeros{' '}
                  <strong className="text-emerald-700" data-id="element-1748">
                    {discountDays} días
                  </strong>{' '}
                  desde la emisión.
                </p>
              </div>
            </div>}

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1749">
            <StatsCard title="Total por Cobrar" value={formatCurrency(totalPendiente)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-1751" />} variant="primary" data-id="element-1750" />
            <StatsCard title="Saldo Vencido" value={formatCurrency(totalVencido)} icon={<AlertCircleIcon className="w-5 h-5" data-id="element-1753" />} variant="danger" data-id="element-1752" />
            <StatsCard title="Facturas Pendientes" value={facturasPendientes} icon={<ClockIcon className="w-5 h-5" data-id="element-1755" />} variant="warning" data-id="element-1754" />
          </div>

          {isFiltered && <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700" data-id="element-1756">
              <FilterIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-1757" />
              <span data-id="element-1758">
                Mostrando <strong data-id="element-1759">{filteredCount}</strong> de{' '}
                <strong data-id="element-1760">{allVisibleInvoices.length}</strong> facturas (filtro
                activo). Los totales de arriba reflejan <strong data-id="element-1761">todas</strong>{' '}
                las facturas.
              </span>
            </div>}

          <div className="flex flex-col gap-3" data-id="element-1762">
            <div className="flex flex-col sm:flex-row gap-3" data-id="element-1763">
              <div className="relative flex-1" data-id="element-1764">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1765" />
                <input type="text" placeholder="Buscar por farmacia, factura o vendedor..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-1766" />
              </div>
              <select value={vendedorFilter} onChange={e => setVendedorFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-1767">
                <option value="all" data-id="element-1768">Todos los vendedores</option>
                {VENDEDORES.map(v => <option key={v} value={v} data-id="element-1769">
                    {v}
                  </option>)}
              </select>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-1770">
                <option value="all" data-id="element-1771">Todos los estados</option>
                <option value="pendiente" data-id="element-1772">Pendiente</option>
                <option value="parcial" data-id="element-1773">Pago Parcial</option>
                <option value="vencida" data-id="element-1774">Vencida</option>
                <option value="pagada" data-id="element-1775">Pagada</option>
              </select>
              <Button variant="secondary" className="ml-auto" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-1777" />} onClick={() => setShowEstadoCuenta(true)} data-id="element-1776">
                Estado de Cuenta
              </Button>
            </div>
            <div className="flex items-center gap-2 flex-wrap" data-id="element-1778">
              <span className="text-xs text-slate-500 font-medium" data-id="element-1779">Desde</span>
              <input type="date" value={dateFrom} onChange={e => {
            setDateFrom(e.target.value);
            setCurrentPage(1);
          }} className="px-2.5 py-1.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-1780" />
              <span className="text-xs text-slate-500 font-medium" data-id="element-1781">Hasta</span>
              <input type="date" value={dateTo} onChange={e => {
            setDateTo(e.target.value);
            setCurrentPage(1);
          }} className="px-2.5 py-1.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-1782" />
              {(dateFrom || dateTo) && <button onClick={() => {
            setDateFrom('');
            setDateTo('');
            setCurrentPage(1);
          }} className="text-xs text-rose-600 hover:text-rose-700 font-medium px-2 py-1 rounded-lg hover:bg-rose-50 transition-colors" data-id="element-1783">
                  Limpiar fechas
                </button>}
            </div>
          </div>

          <Card padding="none" data-id="element-1784">
            {filteredInvoices.length === 0 ? <div className="p-16 text-center" data-id="element-1785">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-1786">
                  <FileTextIcon className="w-8 h-8 text-slate-400" data-id="element-1787" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-1788">
                  No hay facturas
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-1789">
                  No se encontraron facturas con los filtros aplicados
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-1790">
                <table className="w-full" data-id="element-1791">
                  <thead data-id="element-1792">
                    <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-1793">
                      <SortableTableHeader label="Factura" sortKey="numeroFactura" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1794" />
                      <SortableTableHeader label="Farmacia" sortKey="farmacia" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1795" />
                      <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1796" />
                      <SortableTableHeader label="Vencimiento" sortKey="fechaVencimiento" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1797" />
                      <SortableTableHeader label="Sub Total" sortKey="subTotal" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1798" />
                      <SortableTableHeader label="ITBIS" sortKey="itbis" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1799" />
                      <SortableTableHeader label="Monto" sortKey="monto" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1800" />
                      <SortableTableHeader label="Saldo" sortKey="saldoPendiente" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1801" />
                      <SortableTableHeader label="Estado" sortKey="estado" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1802" />
                      <th className="px-5 py-4 w-20" data-id="element-1803"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-1804">
                    {paginatedInvoices.map(invoice => <tr key={invoice.id} className={`hover:bg-slate-50/50 transition-colors ${invoice.estado === 'vencida' ? 'bg-rose-50/30' : ''}`} data-id="element-1805">
                        <td className="px-5 py-4 font-medium text-slate-900" data-id="element-1806">
                          {invoice.numeroFactura}
                        </td>
                        <td className="px-5 py-4 font-medium text-slate-900" data-id="element-1807">
                          {invoice.farmacia}
                        </td>
                        <td className="px-5 py-4" data-id="element-1808">
                          <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-1809">
                            <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-1810" />
                            {invoice.vendedor}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-600" data-id="element-1811">
                          {formatDate(invoice.fechaVencimiento)}
                        </td>
                        <td className="px-5 py-4 text-right text-slate-600 text-sm" data-id="element-1812">
                          {formatCurrency(invoice.subTotal)}
                        </td>
                        <td className="px-5 py-4 text-right text-slate-600 text-sm" data-id="element-1813">
                          {formatCurrency(invoice.itbis)}
                        </td>
                        <td className="px-5 py-4 text-right text-slate-700" data-id="element-1814">
                          {formatCurrency(invoice.monto)}
                        </td>
                        <td className="px-5 py-4 text-right font-bold text-slate-900" data-id="element-1815">
                          {formatCurrency(invoice.saldoPendiente)}
                        </td>
                        <td className="px-5 py-4 text-center" data-id="element-1816">
                          {getStatusBadge(invoice.estado)}
                        </td>
                        <td className="px-5 py-4" data-id="element-1817">
                          <div className="flex items-center gap-1" data-id="element-1818">
                            <button onClick={() => openEditModal(invoice)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1819">
                              <EditIcon className="w-4 h-4" data-id="element-1820" />
                            </button>
                            <button onClick={() => {
                      setEditingInvoice(invoice);
                      setIsDeleteModalOpen(true);
                    }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-1821">
                              <TrashIcon className="w-4 h-4" data-id="element-1822" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </Card>
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredInvoices.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-1823" />
        </>}

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: NOTAS DE CRÉDITO */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'notas-credito' && <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1824">
            <StatsCard title="NC Disponibles" value={ncDisponibles.length} icon={<FileMinusIcon className="w-5 h-5" data-id="element-1826" />} variant="success" data-id="element-1825" />
            <StatsCard title="NC Aplicadas" value={ncAplicadas.length} icon={<CheckCircleIcon className="w-5 h-5" data-id="element-1828" />} variant="primary" data-id="element-1827" />
            <StatsCard title="Monto Disponible" value={formatCurrency(ncMontoDisponible)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-1830" />} variant="warning" data-id="element-1829" />
          </div>

          <div className="flex flex-col sm:flex-row gap-3" data-id="element-1831">
            <div className="relative flex-1" data-id="element-1832">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1833" />
              <input type="text" placeholder="Buscar por farmacia o número de NC..." value={ncSearchQuery} onChange={e => setNCSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-1834" />
            </div>
            <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1836" />} onClick={openAddNCModal} data-id="element-1835">
              Nueva NC
            </Button>
          </div>

          <Card padding="none" data-id="element-1837">
            {filteredNC.length === 0 ? <div className="p-16 text-center" data-id="element-1838">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-1839">
                  <FileMinusIcon className="w-8 h-8 text-slate-400" data-id="element-1840" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-1841">
                  No hay notas de crédito
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-1842">
                  Crea una nueva nota de crédito para comenzar
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-1843">
                <table className="w-full" data-id="element-1844">
                  <thead data-id="element-1845">
                    <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-1846">
                      <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1847">
                        Número
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1848">
                        Cód. Cliente
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1849">
                        Farmacia
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1850">
                        Vendedor
                      </th>
                      <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-1851">
                        Monto Original
                      </th>
                      <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-1852">
                        Disponible
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-1853">
                        Factura Aplicada
                      </th>
                      <th className="px-5 py-4 text-center text-xs font-semibold text-slate-500 uppercase" data-id="element-1854">
                        Estado
                      </th>
                      <th className="px-5 py-4 w-20" data-id="element-1855"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-1856">
                    {paginatedNC.map(nc => <tr key={nc.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-1857">
                        <td className="px-5 py-4 font-medium text-slate-900" data-id="element-1858">
                          {nc.numero}
                        </td>
                        <td className="px-5 py-4" data-id="element-1859">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-mono font-semibold bg-slate-100 text-slate-700" data-id="element-1860">
                            {clients.find(c => c.nombre === nc.farmacia)?.codigo || '—'}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-slate-700" data-id="element-1861">
                          {nc.farmacia}
                        </td>
                        <td className="px-5 py-4" data-id="element-1862">
                          <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-1863">
                            <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-1864" />
                            {nc.vendedor}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-right text-rose-600 font-medium" data-id="element-1865">
                          -{formatCurrency(nc.monto)}
                        </td>
                        <td className="px-5 py-4 text-right font-bold text-slate-900" data-id="element-1866">
                          {formatCurrency(nc.montoDisponible)}
                        </td>
                        <td className="px-5 py-4 text-slate-600" data-id="element-1867">
                          {nc.facturaAplicada || '—'}
                        </td>
                        <td className="px-5 py-4 text-center" data-id="element-1868">
                          {getNCStatusBadge(nc.estado)}
                        </td>
                        <td className="px-5 py-4" data-id="element-1869">
                          <div className="flex items-center gap-1" data-id="element-1870">
                            <button onClick={() => openEditNCModal(nc)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1871">
                              <EditIcon className="w-4 h-4" data-id="element-1872" />
                            </button>
                            <button onClick={() => {
                      setEditingNC(nc);
                      setIsNCDeleteModalOpen(true);
                    }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-1873">
                              <TrashIcon className="w-4 h-4" data-id="element-1874" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </Card>
          <Pagination currentPage={ncPage} totalPages={ncTotalPages} onPageChange={setNCPage} totalItems={filteredNC.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-1875" />
        </>}

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: APLICAR NC */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'aplicar-nc' && <>
          {applicationSuccess ? <Card className="text-center py-12" data-id="element-1876">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1877">
                <CheckCircleIcon className="w-10 h-10 text-emerald-600" data-id="element-1878" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-1879">
                ¡Aplicación Exitosa!
              </h3>
              <p className="text-slate-600 mb-6" data-id="element-1880">
                La nota de crédito y descuento han sido aplicados correctamente.
              </p>
              <Button onClick={() => {
          setApplicationSuccess(false);
          setAppFarmacia('');
        }} data-id="element-1881">
                Nueva Aplicación
              </Button>
            </Card> : <>
              {/* Available NCs Summary — only shown after selecting a farmacia */}
              {appFarmacia && (() => {
          const farmaciaAvailableNCs = creditNotes.filter(nc => (nc.estado === 'disponible' || nc.estado === 'parcial') && nc.montoDisponible > 0 && farmaciaMatch(nc.farmacia, appFarmacia));
          if (farmaciaAvailableNCs.length === 0) return null;
          const totalDisponible = farmaciaAvailableNCs.reduce((sum, nc) => sum + nc.montoDisponible, 0);
          return <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-1882">
                      <div className="flex items-center gap-2 mb-3" data-id="element-1883">
                        <FileMinusIcon className="w-5 h-5 text-amber-600" data-id="element-1884" />
                        <h3 className="font-semibold text-amber-900" data-id="element-1885">
                          Notas de Crédito Disponibles para {appFarmacia} (
                          {farmaciaAvailableNCs.length})
                        </h3>
                      </div>
                      <div className="flex flex-wrap gap-2" data-id="element-1886">
                        {farmaciaAvailableNCs.map(nc => <div key={nc.id} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-white text-amber-800 border border-amber-200" data-id="element-1887">
                            <span className="font-semibold" data-id="element-1888">{nc.numero}</span>
                            <span className="text-xs text-amber-600" data-id="element-1889">
                              {formatCurrency(nc.montoDisponible)}
                            </span>
                          </div>)}
                      </div>
                      <p className="text-xs text-amber-700 font-semibold mt-2" data-id="element-1890">
                        Total disponible: {formatCurrency(totalDisponible)}
                      </p>
                    </div>;
        })()}

              {/* Step 1: Select Farmacia */}
              <Card data-id="element-1891">
                <div className="flex items-center gap-3 mb-4" data-id="element-1892">
                  <div className="w-8 h-8 bg-[#2952C4] text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-1893">
                    1
                  </div>
                  <h3 className="font-semibold text-slate-900" data-id="element-1894">
                    Seleccionar Farmacia
                  </h3>
                </div>
                <SearchableSelect label="" value={appFarmacia} onChange={val => {
            setAppFarmacia(val);
            setSelectedInvoiceId(null);
            setSelectedNCIds([]);
          }} options={FARMACIAS} placeholder="Buscar farmacia..." data-id="element-1895" />
              </Card>

              {appFarmacia && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1896">
                  {/* Step 2: Select Invoice */}
                  <Card data-id="element-1897">
                    <div className="flex items-center gap-3 mb-4" data-id="element-1898">
                      <div className="w-8 h-8 bg-[#2952C4] text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-1899">
                        2
                      </div>
                      <h3 className="font-semibold text-slate-900" data-id="element-1900">
                        Seleccionar Factura
                      </h3>
                    </div>
                    {appInvoices.length === 0 ? <div className="p-8 text-center bg-slate-50 rounded-xl" data-id="element-1901">
                        <CheckCircleIcon className="w-8 h-8 text-emerald-400 mx-auto mb-2" data-id="element-1902" />
                        <p className="text-slate-600 font-medium" data-id="element-1903">
                          No hay facturas pendientes
                        </p>
                      </div> : <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1904">
                        <table className="w-full text-sm" data-id="element-1905">
                          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1906">
                            <tr data-id="element-1907">
                              <th className="px-3 py-2 w-10" data-id="element-1908"></th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-500" data-id="element-1909">
                                Nº Factura
                              </th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-500" data-id="element-1910">
                                Subtotal
                              </th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-500" data-id="element-1911">
                                ITBIS
                              </th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-500" data-id="element-1912">
                                Total
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100" data-id="element-1913">
                            {appInvoices.map(inv => <tr key={inv.id} className={`cursor-pointer transition-colors ${selectedInvoiceId === inv.id ? 'bg-blue-50' : 'hover:bg-slate-50'}`} onClick={() => setSelectedInvoiceId(inv.id)} data-id="element-1914">
                                <td className="px-3 py-3 text-center" data-id="element-1915">
                                  <input type="radio" name="invoice" checked={selectedInvoiceId === inv.id} onChange={() => setSelectedInvoiceId(inv.id)} className="w-4 h-4 text-[#2952C4]" data-id="element-1916" />
                                </td>
                                <td className="px-3 py-3 font-medium text-slate-900" data-id="element-1917">
                                  {inv.numeroFactura}
                                </td>
                                <td className="px-3 py-3 text-right text-slate-700" data-id="element-1918">
                                  {formatCurrency(inv.subTotal)}
                                </td>
                                <td className="px-3 py-3 text-right text-slate-600" data-id="element-1919">
                                  {formatCurrency(inv.itbis)}
                                </td>
                                <td className="px-3 py-3 text-right font-bold text-slate-900" data-id="element-1920">
                                  {formatCurrency(inv.monto)}
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </div>}
                  </Card>

                  {/* Step 3: Select NC (optional) */}
                  <Card data-id="element-1921">
                    <div className="flex items-center gap-3 mb-4" data-id="element-1922">
                      <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-1923">
                        3
                      </div>
                      <h3 className="font-semibold text-slate-900" data-id="element-1924">
                        Seleccionar Nota de Crédito{' '}
                        <span className="text-slate-400 font-normal" data-id="element-1925">
                          (opcional)
                        </span>
                      </h3>
                    </div>
                    {appCreditNotes.length === 0 ? <div className="p-8 text-center bg-slate-50 rounded-xl" data-id="element-1926">
                        <FileMinusIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-1927" />
                        <p className="text-slate-500 font-medium" data-id="element-1928">
                          No hay NC disponibles para esta farmacia
                        </p>
                      </div> : <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1929">
                        <table className="w-full text-sm" data-id="element-1930">
                          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1931">
                            <tr data-id="element-1932">
                              <th className="px-3 py-2 w-10" data-id="element-1933"></th>
                              <th className="px-3 py-2 text-left font-semibold text-slate-500" data-id="element-1934">
                                Nº NC
                              </th>
                              <th className="px-3 py-2 text-right font-semibold text-slate-500" data-id="element-1935">
                                Disponible
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100" data-id="element-1936">
                            {appCreditNotes.map(nc => {
                    const isSelected = selectedNCIds.includes(nc.id);
                    const toggleNC = () => setSelectedNCIds(prev => isSelected ? prev.filter(id => id !== nc.id) : [...prev, nc.id]);
                    return <tr key={nc.id} className={`cursor-pointer transition-colors ${isSelected ? 'bg-amber-50' : 'hover:bg-slate-50'}`} onClick={toggleNC} data-id="element-1937">
                                  <td className="px-3 py-3 text-center" data-id="element-1938">
                                    <input type="checkbox" checked={isSelected} onChange={toggleNC} className="w-4 h-4 text-amber-500 rounded" data-id="element-1939" />
                                  </td>
                                  <td className="px-3 py-3 font-medium text-slate-900" data-id="element-1940">
                                    {nc.numero}
                                  </td>
                                  <td className="px-3 py-3 text-right font-bold text-rose-600" data-id="element-1941">
                                    -{formatCurrency(nc.montoDisponible)}
                                  </td>
                                </tr>;
                  })}
                          </tbody>
                        </table>
                      </div>}
                  </Card>
                </div>}

              {/* Calculation Breakdown */}
              {selectedInvoice && calculation && <Card className="border-2 border-[#2952C4]/20 bg-gradient-to-br from-blue-50/50 to-white" data-id="element-1942">
                  <div className="flex items-center gap-3 mb-6" data-id="element-1943">
                    <div className="w-10 h-10 bg-[#2952C4] text-white rounded-xl flex items-center justify-center" data-id="element-1944">
                      <CalculatorIcon className="w-5 h-5" data-id="element-1945" />
                    </div>
                    <div data-id="element-1946">
                      <h3 className="font-bold text-slate-900" data-id="element-1947">
                        Resumen de Cálculo
                      </h3>
                      <p className="text-sm text-slate-500" data-id="element-1948">
                        Factura {selectedInvoice.numeroFactura}{' '}
                        {selectedNCs.length > 0 ? `+ ${selectedNCs.map(nc => nc.numero).join(', ')}` : ''}
                      </p>
                    </div>
                  </div>

                  <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-id="element-1949">
                    <table className="w-full" data-id="element-1950">
                      <tbody className="divide-y divide-slate-100" data-id="element-1951">
                        <tr className="bg-slate-50" data-id="element-1952">
                          <td className="px-5 py-3 text-sm text-slate-600" data-id="element-1953">
                            Subtotal original
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-slate-900" data-id="element-1954">
                            {formatCurrency(calculation.subtotalOriginal)}
                          </td>
                        </tr>
                        {calculation.ncBreakdown.map(ncItem => <tr key={ncItem.id} className={ncItem.montoAplicado > 0 ? 'bg-rose-50' : 'bg-slate-50'} data-id="element-1955">
                            <td className="px-5 py-3 text-sm" data-id="element-1956">
                              <span className={ncItem.montoAplicado > 0 ? 'text-rose-700' : 'text-slate-400'} data-id="element-1957">
                                NC {ncItem.numero}
                              </span>
                              {ncItem.montoAplicado === 0 && <span className="ml-2 text-xs text-slate-400" data-id="element-1958">
                                  (sin saldo restante)
                                </span>}
                            </td>
                            <td className={`px-5 py-3 text-right font-bold ${ncItem.montoAplicado > 0 ? 'text-rose-600' : 'text-slate-400'}`} data-id="element-1959">
                              {ncItem.montoAplicado > 0 ? `(${formatCurrency(ncItem.montoAplicado)})` : '$0.00'}
                            </td>
                          </tr>)}
                        <tr data-id="element-1960">
                          <td className="px-5 py-3 text-sm text-slate-600" data-id="element-1961">
                            Subtotal ajustado
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-slate-900" data-id="element-1962">
                            {formatCurrency(calculation.subtotalAjustado)}
                          </td>
                        </tr>
                        {calculation.descuentoMonto > 0 && <tr className="bg-emerald-50" data-id="element-1963">
                            <td className="px-5 py-3 text-sm text-emerald-700" data-id="element-1964">
                              Descuento {calculation.descuentoPct}%
                            </td>
                            <td className="px-5 py-3 text-right font-bold text-emerald-600" data-id="element-1965">
                              ({formatCurrency(calculation.descuentoMonto)})
                            </td>
                          </tr>}
                        {calculation.descuentoMonto > 0 && <tr data-id="element-1966">
                            <td className="px-5 py-3 text-sm text-slate-600" data-id="element-1967">
                              Subtotal final
                            </td>
                            <td className="px-5 py-3 text-right font-bold text-slate-900" data-id="element-1968">
                              {formatCurrency(calculation.subtotalFinal)}
                            </td>
                          </tr>}
                        <tr className="bg-slate-50" data-id="element-1969">
                          <td className="px-5 py-3 text-sm text-slate-600" data-id="element-1970">
                            ITBIS (fijo)
                          </td>
                          <td className="px-5 py-3 text-right font-medium text-slate-700" data-id="element-1971">
                            {formatCurrency(calculation.itbisFinal)}
                          </td>
                        </tr>
                        <tr className="bg-[#2952C4]" data-id="element-1972">
                          <td className="px-5 py-4 text-sm font-bold text-white" data-id="element-1973">
                            TOTAL A PAGAR
                          </td>
                          <td className="px-5 py-4 text-right text-xl font-bold text-white" data-id="element-1974">
                            {formatCurrency(calculation.totalAPagar)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-6 flex justify-end" data-id="element-1975">
                    <Button onClick={handleApplyNC} isLoading={isApplying} leftIcon={<CheckCircleIcon className="w-4 h-4" data-id="element-1977" />} data-id="element-1976">
                      Aplicar y Guardar
                    </Button>
                  </div>
                </Card>}
            </>}
        </>}

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* TAB: HISTORIAL NC */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'historial-nc' && (() => {
      // Filter NC applications by vendor — non-admins only see their own
      const filteredNcApps = isAdmin ? ncApplications : ncApplications.filter(ncApp => {
        // Match by invoice vendor
        const invoice = invoices.find(inv => inv.id === ncApp.invoice_id);
        if (invoice && invoice.vendedorId === user?.id) return true;
        // Fallback: match by credit note vendor
        const creditNote = creditNotes.find(nc => nc.id === ncApp.credit_note_id);
        if (creditNote && creditNote.vendedorId === user?.id) return true;
        // Fallback: match by aplicado_por name
        if (ncApp.aplicado_por === user?.nombre) return true;
        return false;
      });
      const ncAppTotalPages = Math.ceil(filteredNcApps.length / ITEMS_PER_PAGE);
      const paginatedNCApps = filteredNcApps.slice((ncAppPage - 1) * ITEMS_PER_PAGE, ncAppPage * ITEMS_PER_PAGE);
      return <>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1978">
                <StatsCard title="Total Aplicaciones" value={filteredNcApps.length} icon={<ArrowRightLeftIcon className="w-5 h-5" data-id="element-1980" />} variant="primary" data-id="element-1979" />
                <StatsCard title="Monto Total Aplicado" value={formatCurrency(filteredNcApps.reduce((sum, nc) => sum + Number(nc.nc_monto_aplicado || 0), 0))} icon={<DollarSignIcon className="w-5 h-5" data-id="element-1982" />} variant="warning" data-id="element-1981" />
                <StatsCard title="Descuento Total" value={formatCurrency(filteredNcApps.reduce((sum, nc) => sum + Number(nc.descuento_monto || 0), 0))} icon={<PercentIcon className="w-5 h-5" data-id="element-1984" />} variant="success" data-id="element-1983" />
              </div>

              <Card padding="none" data-id="element-1985">
                {filteredNcApps.length === 0 ? <div className="p-16 text-center" data-id="element-1986">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-1987">
                      <ArrowRightLeftIcon className="w-8 h-8 text-slate-400" data-id="element-1988" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-1989">
                      No hay aplicaciones
                    </h3>
                    <p className="text-slate-500 text-sm" data-id="element-1990">
                      No se han aplicado notas de crédito a facturas aún
                    </p>
                  </div> : <>
                    {/* Mobile cards */}
                    <div className="md:hidden divide-y divide-slate-100" data-id="element-1991">
                      {paginatedNCApps.map(ncApp => <div key={ncApp.id} className="p-4" data-id="element-1992">
                          <div className="flex items-start justify-between gap-2" data-id="element-1993">
                            <div className="flex-1 min-w-0" data-id="element-1994">
                              <p className="font-semibold text-slate-900 text-sm" data-id="element-1995">
                                NC: {ncApp.credit_note_numero}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5" data-id="element-1996">
                                → Factura: {ncApp.invoice_numero}
                              </p>
                              <p className="text-xs text-slate-400 mt-0.5" data-id="element-1997">
                                {ncApp.farmacia}
                              </p>
                            </div>
                            <button onClick={() => setAnularNCApp(ncApp)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors flex-shrink-0" title="Anular aplicación" data-id="element-1998">
                              <UndoIcon className="w-4 h-4" data-id="element-1999" />
                            </button>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-2 text-xs" data-id="element-2000">
                            <div className="bg-rose-50 rounded-lg p-2" data-id="element-2001">
                              <p className="text-rose-500 mb-0.5" data-id="element-2002">
                                NC Aplicada
                              </p>
                              <p className="font-bold text-rose-700" data-id="element-2003">
                                -
                                {formatCurrency(Number(ncApp.nc_monto_aplicado || 0))}
                              </p>
                            </div>
                            <div className="bg-emerald-50 rounded-lg p-2" data-id="element-2004">
                              <p className="text-emerald-500 mb-0.5" data-id="element-2005">
                                Desc. {Number(ncApp.descuento_pct || 0)}%
                              </p>
                              <p className="font-bold text-emerald-700" data-id="element-2006">
                                -
                                {formatCurrency(Number(ncApp.descuento_monto || 0))}
                              </p>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-2" data-id="element-2007">
                              <p className="text-slate-400 mb-0.5" data-id="element-2008">
                                Total Original
                              </p>
                              <p className="font-bold text-slate-700" data-id="element-2009">
                                {formatCurrency(Number(ncApp.total_original || 0))}
                              </p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-2" data-id="element-2010">
                              <p className="text-blue-500 mb-0.5" data-id="element-2011">
                                Total a Pagar
                              </p>
                              <p className="font-bold text-blue-700" data-id="element-2012">
                                {formatCurrency(Number(ncApp.total_a_pagar || 0))}
                              </p>
                            </div>
                          </div>
                          <p className="text-xs text-slate-400 mt-2" data-id="element-2013">
                            {new Date(ncApp.fecha_aplicacion || ncApp.created_at).toLocaleDateString('es-DO', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}
                            {' · '}Por: {ncApp.aplicado_por}
                          </p>
                        </div>)}
                    </div>

                    {/* Desktop table */}
                    <div className="hidden md:block overflow-x-auto" data-id="element-2014">
                      <table className="w-full" data-id="element-2015">
                        <thead data-id="element-2016">
                          <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-2017">
                            <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2018">
                              Fecha
                            </th>
                            <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2019">
                              NC
                            </th>
                            <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2020">
                              Factura
                            </th>
                            <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2021">
                              Farmacia
                            </th>
                            <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2022">
                              Total Original
                            </th>
                            <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2023">
                              NC Aplicada
                            </th>
                            <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2024">
                              Descuento
                            </th>
                            <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2025">
                              Total a Pagar
                            </th>
                            <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2026">
                              Aplicado Por
                            </th>
                            <th className="px-5 py-4 w-16" data-id="element-2027"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100" data-id="element-2028">
                          {paginatedNCApps.map(ncApp => <tr key={ncApp.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-2029">
                              <td className="px-5 py-4 text-sm text-slate-600" data-id="element-2030">
                                {new Date(ncApp.fecha_aplicacion || ncApp.created_at).toLocaleDateString('es-DO', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                              </td>
                              <td className="px-5 py-4" data-id="element-2031">
                                <span className="font-semibold text-slate-900 text-sm" data-id="element-2032">
                                  {ncApp.credit_note_numero}
                                </span>
                              </td>
                              <td className="px-5 py-4" data-id="element-2033">
                                <span className="font-medium text-slate-700 text-sm" data-id="element-2034">
                                  {ncApp.invoice_numero}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-sm text-slate-600" data-id="element-2035">
                                {ncApp.farmacia}
                              </td>
                              <td className="px-5 py-4 text-right text-sm text-slate-700" data-id="element-2036">
                                {formatCurrency(Number(ncApp.total_original || 0))}
                              </td>
                              <td className="px-5 py-4 text-right" data-id="element-2037">
                                <span className="text-sm font-semibold text-rose-600" data-id="element-2038">
                                  -
                                  {formatCurrency(Number(ncApp.nc_monto_aplicado || 0))}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-right" data-id="element-2039">
                                <span className="text-sm font-semibold text-emerald-600" data-id="element-2040">
                                  -
                                  {formatCurrency(Number(ncApp.descuento_monto || 0))}
                                </span>
                                {Number(ncApp.descuento_pct) > 0 && <span className="text-xs text-slate-400 ml-1" data-id="element-2041">
                                    ({ncApp.descuento_pct}%)
                                  </span>}
                              </td>
                              <td className="px-5 py-4 text-right" data-id="element-2042">
                                <span className="text-sm font-bold text-[#2952C4]" data-id="element-2043">
                                  {formatCurrency(Number(ncApp.total_a_pagar || 0))}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-sm text-slate-500" data-id="element-2044">
                                {ncApp.aplicado_por}
                              </td>
                              <td className="px-5 py-4" data-id="element-2045">
                                <button onClick={() => setAnularNCApp(ncApp)} title="Anular aplicación de NC" className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-2046">
                                  <UndoIcon className="w-4 h-4" data-id="element-2047" />
                                </button>
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </>}
              </Card>
              {filteredNcApps.length > ITEMS_PER_PAGE && <Pagination currentPage={ncAppPage} totalPages={ncAppTotalPages} onPageChange={setNcAppPage} totalItems={filteredNcApps.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-2048" />}
            </>;
    })()}

      {/* Anular NC Application Modal */}
      <Modal isOpen={!!anularNCApp} onClose={() => setAnularNCApp(null)} title="Anular Aplicación de Nota de Crédito" size="sm" data-id="element-2049">
        <ModalBody data-id="element-2050">
          <div className="text-center space-y-3" data-id="element-2051">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-rose-50 rounded-2xl" data-id="element-2052">
              <UndoIcon className="w-7 h-7 text-rose-500" data-id="element-2053" />
            </div>
            <p className="text-slate-600" data-id="element-2054">
              ¿Estás seguro de anular la aplicación de la nota de crédito{' '}
              <strong data-id="element-2055">{anularNCApp?.credit_note_numero}</strong> a la factura{' '}
              <strong data-id="element-2056">{anularNCApp?.invoice_numero}</strong>?
            </p>
            <div className="bg-slate-50 rounded-xl p-3 text-sm space-y-1" data-id="element-2057">
              <p className="text-slate-500" data-id="element-2058">
                Farmacia:{' '}
                <strong className="text-slate-700" data-id="element-2059">
                  {anularNCApp?.farmacia}
                </strong>
              </p>
              <p className="text-slate-500" data-id="element-2060">
                NC aplicada:{' '}
                <strong className="text-rose-600" data-id="element-2061">
                  -{formatCurrency(Number(anularNCApp?.nc_monto_aplicado || 0))}
                </strong>
              </p>
              <p className="text-slate-500" data-id="element-2062">
                Descuento:{' '}
                <strong className="text-emerald-600" data-id="element-2063">
                  -{formatCurrency(Number(anularNCApp?.descuento_monto || 0))}
                </strong>
              </p>
            </div>
            <p className="text-xs text-slate-400" data-id="element-2064">
              Se restaurará el saldo de la factura y el monto disponible de la
              nota de crédito.
            </p>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-2065">
          <Button variant="secondary" onClick={() => setAnularNCApp(null)} data-id="element-2066">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleAnularNCApplication} isLoading={isAnulandoNC} leftIcon={<UndoIcon className="w-4 h-4" data-id="element-2068" />} data-id="element-2067">
            Anular Aplicación
          </Button>
        </ModalFooter>
      </Modal>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/* MODALS */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}

      {/* Invoice Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingInvoice ? 'Editar Factura' : 'Nueva Factura'} size="lg" data-id="element-2069">
        <ModalBody data-id="element-2070">
          <div className="space-y-4" data-id="element-2071">
            {facturaError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-2072">
                <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-2073" />
                {facturaError}
              </div>}
            {!editingInvoice && <button type="button" onClick={() => setIsCotizacionModalOpen(true)} className="group w-full relative overflow-hidden rounded-2xl border-2 border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 text-left transition-all duration-300 hover:border-indigo-300 hover:shadow-lg hover:shadow-indigo-100/50 hover:scale-[1.01] active:scale-[0.99]" data-id="element-2074">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-indigo-100/60 to-transparent rounded-bl-full -translate-y-4 translate-x-4 group-hover:translate-x-2 group-hover:-translate-y-2 transition-transform duration-500" data-id="element-2075" />
                <div className="relative flex items-center gap-4" data-id="element-2076">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-md shadow-indigo-200/60 group-hover:shadow-lg group-hover:shadow-indigo-300/60 transition-shadow duration-300" data-id="element-2077">
                    <SearchIcon className="w-5 h-5 text-white" data-id="element-2078" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2079">
                    <p className="font-bold text-slate-900 text-sm group-hover:text-indigo-900 transition-colors" data-id="element-2080">
                      Buscar Cotización (Pedido)
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5 group-hover:text-indigo-400 transition-colors" data-id="element-2081">
                      Importar datos desde un pedido existente
                    </p>
                  </div>
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-colors" data-id="element-2082">
                    <svg className="w-4 h-4 text-indigo-400 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition-all duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} data-id="element-2083">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" data-id="element-2084" />
                    </svg>
                  </div>
                </div>
              </button>}
            <div className="grid grid-cols-2 gap-4" data-id="element-2085">
              <Input label="Número de Factura" value={formData.numeroFactura} onChange={e => {
              setFacturaError('');
              setFormData({
                ...formData,
                numeroFactura: e.target.value
              });
            }} placeholder="FAC-2024-001" data-id="element-2086" />
              <div data-id="element-2087">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2088">
                  Estado
                </label>
                <select value={formData.estado} onChange={e => {
                const newEstado = e.target.value as Invoice['estado'];
                setFormData({
                  ...formData,
                  estado: newEstado
                });
              }} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2089">
                  <option value="pendiente" data-id="element-2090">Pendiente</option>
                  <option value="parcial" data-id="element-2091">Pago Parcial</option>
                  <option value="vencida" data-id="element-2092">Vencida</option>
                  <option value="pagada" data-id="element-2093">Pagada</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-2094">
              <SearchableSelect label="Farmacia" value={formData.farmacia} onChange={val => setFormData({
              ...formData,
              farmacia: val
            })} options={FARMACIAS} placeholder="Buscar farmacia..." data-id="element-2095" />
              <div data-id="element-2096">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2097">
                  Vendedor
                </label>
                <select value={formData.vendedorId} onChange={e => {
                const selectedUser = users.find(u => u.id === e.target.value);
                setFormData({
                  ...formData,
                  vendedorId: e.target.value,
                  vendedor: selectedUser?.nombre || ''
                });
              }} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2098">
                  <option value="" data-id="element-2099">Seleccionar vendedor...</option>
                  {users.filter(u => u.activo && u.rolNombre !== 'Administrador' && u.permisos?.['new-visit'] === true).map(u => <option key={u.id} value={u.id} data-id="element-2100">
                        {u.nombre}
                      </option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-2101">
              <Input label="Fecha de Emisión" type="date" value={formData.fechaEmision} onChange={e => setFormData({
              ...formData,
              fechaEmision: e.target.value
            })} data-id="element-2102" />
              <Input label="Fecha de Vencimiento" type="date" value={formData.fechaVencimiento} min={!editingInvoice ? new Date().toISOString().split('T')[0] : undefined} onChange={e => {
              const newFechaVenc = e.target.value;
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const fechaVenc = new Date(newFechaVenc);
              const saldoPend = Number(formData.saldoPendiente);
              const monto = Number(formData.monto);
              let newEstado = formData.estado;
              if (formData.estado === 'vencida' && fechaVenc >= today) {
                // Fecha movida al futuro → ya no está vencida
                newEstado = saldoPend < monto && saldoPend > 0 ? 'parcial' : 'pendiente';
              } else if (formData.estado !== 'pagada' && fechaVenc < today && saldoPend > 0) {
                // Fecha movida al pasado → marcar como vencida
                newEstado = 'vencida';
              }
              setFormData({
                ...formData,
                fechaVencimiento: newFechaVenc,
                estado: newEstado
              });
            }} data-id="element-2103" />
            </div>
            <div className="grid grid-cols-3 gap-4" data-id="element-2104">
              <Input label="Sub Total (DOP)" type="number" value={formData.subTotal} onChange={e => {
              const sub = Number(e.target.value) || 0;
              const tax = Math.round(sub * 0.18 * 100) / 100;
              const total = Math.round((sub + tax) * 100) / 100;
              setFormData({
                ...formData,
                subTotal: e.target.value,
                itbis: String(tax),
                monto: String(total),
                saldoPendiente: editingInvoice ? formData.saldoPendiente : String(total)
              });
            }} placeholder="0" data-id="element-2105" />
              <Input label="ITBIS 18% (DOP)" type="number" value={formData.itbis} onChange={e => {
              const tax = Number(e.target.value) || 0;
              const sub = Number(formData.subTotal) || 0;
              const total = Math.round((sub + tax) * 100) / 100;
              setFormData({
                ...formData,
                itbis: e.target.value,
                monto: String(total),
                saldoPendiente: editingInvoice ? formData.saldoPendiente : String(total)
              });
            }} placeholder="0" data-id="element-2106" />
              <div data-id="element-2107">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2108">
                  Monto Total (DOP)
                </label>
                <div className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 font-bold" data-id="element-2109">
                  {formatCurrency(Number(formData.monto) || 0)}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-2110">
              <Input label="Saldo Pendiente (DOP)" type="number" value={formData.saldoPendiente} onChange={e => setFormData({
              ...formData,
              saldoPendiente: e.target.value
            })} placeholder="0" data-id="element-2111" />
              <div data-id="element-2112">
                <Input label="PP % (Pronto Pago)" type="number" value={formData.ppPct} onChange={e => setFormData({
                ...formData,
                ppPct: e.target.value
              })} placeholder={`Global: ${discountPct}%`} data-id="element-2113" />
                <p className="text-xs text-slate-400 mt-1" data-id="element-2114">
                  Dejar vacío para usar el % global ({discountPct}%)
                </p>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-2115">
          <Button variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-2116">
            Cancelar
          </Button>
          <Button onClick={handleSave} isLoading={isSaving} disabled={!formData.numeroFactura || !formData.monto || !formData.fechaVencimiento} data-id="element-2117">
            {editingInvoice ? 'Guardar Cambios' : 'Crear Factura'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Invoice Delete Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Factura" size="sm" data-id="element-2118">
        <ModalBody data-id="element-2119">
          <p className="text-slate-600" data-id="element-2120">
            ¿Eliminar la factura{' '}
            <strong data-id="element-2121">{editingInvoice?.numeroFactura}</strong>? Esta acción no se
            puede deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-2122">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} data-id="element-2123">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} data-id="element-2124">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>

      {/* NC Add/Edit Modal */}
      <Modal isOpen={isNCModalOpen} onClose={() => setIsNCModalOpen(false)} title={editingNC ? 'Editar Nota de Crédito' : 'Nueva Nota de Crédito'} size="md" data-id="element-2125">
        <ModalBody data-id="element-2126">
          <div className="space-y-4" data-id="element-2127">
            {ncError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-2128">
                <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-2129" />
                {ncError}
              </div>}
            <Input label="Número de NC" value={ncFormData.numero} onChange={e => {
            setNCError('');
            setNCFormData({
              ...ncFormData,
              numero: e.target.value
            });
          }} placeholder="NC-00001" data-id="element-2130" />
            <SearchableSelect label="Farmacia" value={ncFormData.farmacia} onChange={val => setNCFormData({
            ...ncFormData,
            farmacia: val
          })} options={FARMACIAS} placeholder="Buscar farmacia..." data-id="element-2131" />
            <div data-id="element-2132">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2133">
                Vendedor
              </label>
              <select value={ncFormData.vendedorId} onChange={e => {
              const selectedUser = users.find(u => u.id === e.target.value);
              setNCFormData({
                ...ncFormData,
                vendedorId: e.target.value,
                vendedor: selectedUser?.nombre || ''
              });
            }} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2134">
                <option value="" data-id="element-2135">Seleccionar vendedor...</option>
                {users.filter(u => u.activo && u.rolNombre !== 'Administrador' && u.permisos?.['new-visit'] === true).map(u => <option key={u.id} value={u.id} data-id="element-2136">
                      {u.nombre}
                    </option>)}
              </select>
            </div>
            <Input label="Monto (DOP)" type="number" value={ncFormData.monto} onChange={e => setNCFormData({
            ...ncFormData,
            monto: e.target.value
          })} placeholder="0" data-id="element-2137" />
            <div data-id="element-2138">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2139">
                Notas
              </label>
              <textarea value={ncFormData.notas} onChange={e => setNCFormData({
              ...ncFormData,
              notas: e.target.value
            })} rows={2} className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Notas opcionales..." data-id="element-2140" />
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-2141">
          <Button variant="secondary" onClick={() => setIsNCModalOpen(false)} data-id="element-2142">
            Cancelar
          </Button>
          <Button onClick={handleSaveNC} isLoading={isSaving} disabled={!ncFormData.numero || !ncFormData.monto || !ncFormData.farmacia} data-id="element-2143">
            {editingNC ? 'Guardar Cambios' : 'Crear NC'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* NC Delete Modal */}
      <Modal isOpen={isNCDeleteModalOpen} onClose={() => setIsNCDeleteModalOpen(false)} title="Eliminar Nota de Crédito" size="sm" data-id="element-2144">
        <ModalBody data-id="element-2145">
          <p className="text-slate-600" data-id="element-2146">
            ¿Eliminar la NC <strong data-id="element-2147">{editingNC?.numero}</strong>? Esta acción no
            se puede deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-2148">
          <Button variant="secondary" onClick={() => setIsNCDeleteModalOpen(false)} data-id="element-2149">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDeleteNC} data-id="element-2150">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Payment Modal */}
      <Modal isOpen={isPaymentModalOpen} onClose={() => {
      setIsPaymentModalOpen(false);
      setPaymentDone(false);
    }} title={paymentDone ? '✓ Pago Registrado' : 'Registrar Pago Múltiple'} size="lg" data-id="element-2151">
        <ModalBody data-id="element-2152">
          {paymentDone && lastPaymentData ? <div className="space-y-5" data-id="element-2153">
              <div className="flex flex-col items-center text-center p-6 bg-emerald-50 border border-emerald-200 rounded-2xl" data-id="element-2154">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4" data-id="element-2155">
                  <CheckCircleIcon className="w-9 h-9 text-emerald-600" data-id="element-2156" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-2157">
                  ¡Pago procesado exitosamente!
                </h3>
                <p className="text-sm text-slate-600" data-id="element-2158">
                  Se registró un pago de{' '}
                  <strong className="text-emerald-700" data-id="element-2159">
                    {formatCurrency(lastPaymentData.total)}
                  </strong>{' '}
                  para <strong data-id="element-2160">{lastPaymentData.farmacia}</strong>
                </p>
                <div className="mt-3 flex flex-wrap gap-2 justify-center" data-id="element-2161">
                  {lastPaymentData.invoices.map(inv => <span key={inv.numeroFactura} className="px-2.5 py-1 bg-white border border-emerald-200 rounded-lg text-xs font-semibold text-emerald-700" data-id="element-2162">
                      {inv.numeroFactura}: {formatCurrency(inv.abono)}
                    </span>)}
                </div>
              </div>
              <div data-id="element-2163">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-2164">
                  Método de pago (para el recibo)
                </label>
                <select value={paymentMetodo} onChange={e => setPaymentMetodo(e.target.value)} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2165">
                  <option value="efectivo" data-id="element-2166">Efectivo</option>
                  <option value="cheque" data-id="element-2167">Cheque</option>
                  <option value="transferencia" data-id="element-2168">Transferencia</option>
                </select>
              </div>
              <button onClick={handleDownloadRecibo} disabled={isDownloadingRecibo} className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#2952C4] hover:bg-[#1E3D96] disabled:opacity-60 text-white font-bold text-base rounded-xl transition-colors shadow-lg" data-id="element-2169">
                {isDownloadingRecibo ? <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" data-id="element-2170" /> : <DownloadIcon className="w-5 h-5" data-id="element-2171" />}
                {isDownloadingRecibo ? 'Generando PDF...' : 'Descargar Recibo PDF'}
              </button>
            </div> : <div className="space-y-6" data-id="element-2172">
              <SearchableSelect label="Seleccionar Cliente" value={paymentClient} onChange={val => {
            setPaymentClient(val);
            setPaymentAmounts({});
          }} options={FARMACIAS} placeholder="Buscar farmacia..." data-id="element-2173" />
              {paymentClient && (clientInvoices.length === 0 ? <div className="p-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200" data-id="element-2174">
                    <CheckCircleIcon className="w-8 h-8 text-emerald-400 mx-auto mb-2" data-id="element-2175" />
                    <p className="text-slate-600 font-medium" data-id="element-2176">
                      Este cliente no tiene facturas pendientes
                    </p>
                  </div> : <div className="space-y-4" data-id="element-2177">
                    <div className="flex items-center justify-between" data-id="element-2178">
                      <h3 className="text-sm font-semibold text-slate-900" data-id="element-2179">
                        Facturas Pendientes
                      </h3>
                      <span className="text-xs text-slate-500" data-id="element-2180">
                        Ingrese el monto a pagar por factura
                      </span>
                    </div>
                    <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-2181">
                      <table className="w-full text-sm" data-id="element-2182">
                        <thead className="bg-slate-50 border-b border-slate-200" data-id="element-2183">
                          <tr data-id="element-2184">
                            <th className="px-4 py-3 text-left font-semibold text-slate-500" data-id="element-2185">
                              Factura
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-500" data-id="element-2186">
                              Vencimiento
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-500" data-id="element-2187">
                              Saldo
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-500 w-40" data-id="element-2188">
                              Abono
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100" data-id="element-2189">
                          {clientInvoices.map(inv => <tr key={inv.id} className="hover:bg-slate-50/50" data-id="element-2190">
                              <td className="px-4 py-3 font-medium text-slate-900" data-id="element-2191">
                                {inv.numeroFactura}
                              </td>
                              <td className="px-4 py-3 text-slate-600" data-id="element-2192">
                                {formatDate(inv.fechaVencimiento)}
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-slate-900" data-id="element-2193">
                                {formatCurrency(inv.saldoPendiente)}
                              </td>
                              <td className="px-4 py-3" data-id="element-2194">
                                <div className="relative" data-id="element-2195">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-2196">
                                    $
                                  </span>
                                  <input type="number" min="0" max={inv.saldoPendiente} placeholder="0" value={paymentAmounts[inv.id] || ''} onChange={e => handlePaymentAmountChange(inv.id, e.target.value)} className="w-full pl-6 pr-3 py-1.5 text-right border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-900" data-id="element-2197" />
                                </div>
                              </td>
                            </tr>)}
                        </tbody>
                        <tfoot className="bg-slate-50 border-t border-slate-200" data-id="element-2198">
                          <tr data-id="element-2199">
                            <td colSpan={3} className="px-4 py-3 text-right font-bold text-slate-700" data-id="element-2200">
                              Total a Pagar:
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-emerald-600 text-lg" data-id="element-2201">
                              {formatCurrency(totalPayment)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>)}
            </div>}
        </ModalBody>
        <ModalFooter data-id="element-2202">
          {paymentDone ? <Button variant="secondary" onClick={() => {
          setIsPaymentModalOpen(false);
          setPaymentDone(false);
        }} data-id="element-2203">
              Cerrar
            </Button> : <>
              <Button variant="secondary" onClick={() => setIsPaymentModalOpen(false)} data-id="element-2204">
                Cancelar
              </Button>
              <Button onClick={handleProcessPayment} isLoading={isSaving} disabled={!paymentClient || totalPayment <= 0} data-id="element-2205">
                Procesar Pago
              </Button>
            </>}
        </ModalFooter>
      </Modal>

      {/* Cotizacion Selection Modal */}
      <Modal isOpen={isCotizacionModalOpen} onClose={() => setIsCotizacionModalOpen(false)} title="Cotizaciones" size="lg" backdropBlur stacked data-id="element-2206">
        <ModalBody data-id="element-2207">
          <div className="space-y-4" data-id="element-2208">
            <div className="relative" data-id="element-2209">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2210" />
              <input type="text" placeholder="Buscar por número o farmacia..." value={cotizacionSearch} onChange={e => setCotizacionSearch(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-2211" />
            </div>
            {isLoadingCotizaciones ? <div className="py-10 text-center" data-id="element-2212">
                <div className="inline-flex items-center justify-center w-10 h-10 bg-slate-100 rounded-xl mb-3" data-id="element-2213">
                  <FileTextIcon className="w-5 h-5 text-slate-400 animate-spin" data-id="element-2214" />
                </div>
                <p className="text-sm text-slate-500" data-id="element-2215">
                  Cargando cotizaciones...
                </p>
              </div> : <div className="overflow-x-auto border border-slate-200 rounded-xl max-h-[400px] overflow-y-auto" data-id="element-2216">
                <table className="w-full" data-id="element-2217">
                  <thead className="sticky top-0" data-id="element-2218">
                    <tr className="bg-slate-50 border-b border-slate-200" data-id="element-2219">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2220">
                        Número
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2221">
                        Farmacia
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2222">
                        Fecha
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2223">
                        Total
                      </th>
                      <th className="px-4 py-3 w-20" data-id="element-2224"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-2225">
                    {cotizaciones.filter(c => (c.numero_cotizacion || '').toLowerCase().includes(cotizacionSearch.toLowerCase()) || (c.farmacia || '').toLowerCase().includes(cotizacionSearch.toLowerCase())).map(cotizacion => <tr key={cotizacion.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => {
                  // Import cotizacion data into the invoice form
                  setFormData(prev => ({
                    ...prev,
                    numeroFactura: cotizacion.numero_cotizacion || '',
                    farmacia: cotizacion.farmacia || '',
                    vendedor: cotizacion.vendedor || '',
                    monto: String(cotizacion.total || 0),
                    saldoPendiente: String(cotizacion.total || 0)
                  }));
                  setIsCotizacionModalOpen(false);
                  setCotizacionSearch('');
                }} data-id="element-2226">
                          <td className="px-4 py-3 font-medium text-slate-900 text-sm" data-id="element-2227">
                            {cotizacion.numero_cotizacion}
                          </td>
                          <td className="px-4 py-3 text-slate-700 text-sm" data-id="element-2228">
                            {cotizacion.farmacia}
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-sm" data-id="element-2229">
                            {cotizacion.fecha ? formatDate(new Date(cotizacion.fecha)) : '—'}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-900 text-sm" data-id="element-2230">
                            {formatCurrency(cotizacion.total || 0)}
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-2231">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-indigo-50 text-indigo-600" data-id="element-2232">
                              Seleccionar
                            </span>
                          </td>
                        </tr>)}
                    {cotizaciones.filter(c => (c.numero_cotizacion || '').toLowerCase().includes(cotizacionSearch.toLowerCase()) || (c.farmacia || '').toLowerCase().includes(cotizacionSearch.toLowerCase())).length === 0 && <tr data-id="element-2233">
                        <td colSpan={5} className="px-4 py-10 text-center" data-id="element-2234">
                          <div className="inline-flex items-center justify-center w-10 h-10 bg-slate-100 rounded-xl mb-2" data-id="element-2235">
                            <FileTextIcon className="w-5 h-5 text-slate-400" data-id="element-2236" />
                          </div>
                          <p className="text-sm text-slate-500" data-id="element-2237">
                            {cotizacionSearch ? 'No se encontraron cotizaciones' : 'No hay cotizaciones disponibles'}
                          </p>
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>}
          </div>
        </ModalBody>
      </Modal>

      {/* Estado de Cuenta Modal */}
      <Modal isOpen={showEstadoCuenta} onClose={() => setShowEstadoCuenta(false)} title="Estado de Cuenta por Cliente" data-id="element-2238">
        <ModalBody data-id="element-2239">
          <div className="space-y-4" data-id="element-2240">
            <SearchableSelect label="Cliente (Farmacia)" options={FARMACIAS.map(f => ({
            value: f,
            label: f
          }))} value={estadoCuentaClient} onChange={setEstadoCuentaClient} placeholder="Seleccionar cliente..." data-id="element-2241" />
            {(dateFrom || dateTo) && <div className="p-3 bg-blue-50 text-blue-700 rounded-xl text-sm" data-id="element-2242">
                <p className="font-medium mb-1" data-id="element-2243">Filtro de fechas activo:</p>
                <p data-id="element-2244">
                  {dateFrom ? formatDate(new Date(dateFrom + 'T00:00:00')) : 'Inicio'}{' '}
                  al{' '}
                  {dateTo ? formatDate(new Date(dateTo + 'T00:00:00')) : 'Hoy'}
                </p>
                <p className="text-xs mt-1 opacity-80" data-id="element-2245">
                  El PDF solo incluirá las facturas emitidas en este rango.
                </p>
              </div>}
          </div>
        </ModalBody>
        <ModalFooter data-id="element-2246">
          <Button variant="secondary" onClick={() => setShowEstadoCuenta(false)} data-id="element-2247">
            Cancelar
          </Button>
          <Button onClick={generateEstadoCuenta} isLoading={isGeneratingEstadoCuenta} disabled={!estadoCuentaClient} data-id="element-2248">
            Generar PDF
          </Button>
        </ModalFooter>
      </Modal>

      {/* Discount Config Modal */}
      {canSeeDiscount && <Modal isOpen={isDiscountConfigOpen} onClose={() => setIsDiscountConfigOpen(false)} title="Configurar Descuento por Pronto Pago" size="sm" data-id="element-2249">
          <ModalBody data-id="element-2250">
            <div className="space-y-4" data-id="element-2251">
              <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl text-sm text-emerald-800" data-id="element-2252">
                El descuento se aplica automáticamente al usar "Aplicar NC".
              </div>
              <div data-id="element-2253">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2254">
                  Porcentaje de descuento (%)
                </label>
                <input type="number" min="0.1" max="50" step="0.5" value={discountConfigDraft.pct} onChange={e => setDiscountConfigDraft({
              ...discountConfigDraft,
              pct: Number(e.target.value)
            })} className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2255" />
              </div>
              <div data-id="element-2256">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2257">
                  Días máximos desde emisión
                </label>
                <input type="number" min="1" max="365" value={discountConfigDraft.days} onChange={e => setDiscountConfigDraft({
              ...discountConfigDraft,
              days: Number(e.target.value)
            })} className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2258" />
              </div>
              <div className="p-3 bg-slate-50 rounded-xl text-sm text-slate-600 text-center" data-id="element-2259">
                Descuento de{' '}
                <strong className="text-emerald-700" data-id="element-2260">
                  {discountConfigDraft.pct}%
                </strong>{' '}
                para cobros antes de los{' '}
                <strong className="text-emerald-700" data-id="element-2261">
                  {discountConfigDraft.days} días
                </strong>
              </div>
            </div>
          </ModalBody>
          <ModalFooter data-id="element-2262">
            <Button variant="secondary" onClick={() => setIsDiscountConfigOpen(false)} data-id="element-2263">
              Cancelar
            </Button>
            <Button onClick={() => {
          saveDiscountConfig(discountConfigDraft.pct, discountConfigDraft.days);
          addActivityLog('Editó descuento pronto pago', `Porcentaje: ${discountConfigDraft.pct}% | Días: ${discountConfigDraft.days}`);
          setIsDiscountConfigOpen(false);
        }} data-id="element-2264">
              Guardar Configuración
            </Button>
          </ModalFooter>
        </Modal>}
    </div>;
}